<template>
  <div class="container-custom" :style="styleObject">
    <header>
      <!-- KSK088: no logout once a payment is running or the order is saved. -->
      <Header :show-logout="canLogout"></Header>
    </header>
    <!-- KSK013: method list (theme 2). Each card keeps the old v-if / @click;
         the payment-in-progress screens below are untouched. -->
    <div v-if="isLoadDone && !isPaymentProcessing" class="ksk-ps__scroll">
      <!-- One summary row; the dining option is already in the header. -->
      <section class="ksk-ps__amount">
        <div class="ksk-ps__amount-text">
          <div class="ksk-ps__amount-label">{{ $t("kiosk.payment.amountToPay") }}</div>
          <div class="ksk-ps__amount-meta">{{ $t(itemCount == 1 ? "kiosk.common.itemOne" : "kiosk.common.itemMany", { count: itemCount }) }}</div>
        </div>
        <div class="ksk-ps__amount-value">{{ formatPrice(total) }}</div>
      </section>

      <h1 class="ksk-ps__title">{{ $t("kiosk.payment.chooseMethod") }}</h1>

      <div class="ksk-ps__methods">
        <div
          v-if="allowEWallet && isLogined && merchantMember?.wa_balance >= 1"
          class="ksk-ps-card"
          v-bind="kskSlot('payment_method')"
          :class="{ 'ksk-ps-card--disabled': isEWalletDisabled }"
          @click="eWalletPayment()"
        >
          <div class="ksk-ps-card__icon ksk-ps-card__icon--wallet">
            <span class="ksk-ps-card__disc">
              <span class="material-icons-round">account_balance_wallet</span>
            </span>
          </div>
          <div class="ksk-ps-card__body">
            <div class="ksk-ps-card__title-row">
              <span class="ksk-ps-card__title">{{ $t("kiosk.payment.eWallet") }}</span>
              <span v-if="isEWalletDisabled" class="ksk-ps-card__pill ksk-ps-card__pill--error">
                {{ $t("kiosk.payment.insufficientBalance") }}
              </span>
            </div>
            <div class="ksk-ps-card__desc">
              {{ $t("kiosk.payment.balance") }}
              <strong class="ksk-ps-card__balance">{{ merchantMember?.amount_format ?? "$ 0.00" }}</strong>
            </div>
            <div class="ksk-ps-card__caption">
              {{ $t("kiosk.payment.otpWhatsapp", { digits: memberInfo?.phone_number?.slice(-4) }) }}
            </div>
          </div>
          <span class="material-icons-round ksk-ps-card__chevron">chevron_right</span>
        </div>

        <template v-if="allowCreditCard || allowPaynowQR || allowShopBack">
          <div
            v-if="allowCreditCard && readerStatus == 'online'"
            class="ksk-ps-card ksk-ps-card--card"
            v-bind="kskSlot('payment_method')"
            @click="verifyReaderState()"
          >
            <div class="ksk-ps-card__icon ksk-ps-card__icon--contactless">
              <span class="material-icons-round">contactless</span>
            </div>
            <div class="ksk-ps-card__body">
              <div class="ksk-ps-card__title">{{ $t("kiosk.payment.contactless") }}</div>
              <div class="ksk-ps-card__desc">{{ $t("kiosk.payment.contactlessDesc") }}</div>
              <div class="ksk-ps-card__logos ksk-ps-card__logos--card">
                <img src="@/assets/visa.png" alt="Visa" />
                <img src="@/assets/card.png" alt="Mastercard" />
                <img src="@/assets/apple-pay.png" alt="Apple Pay" />
                <img src="@/assets/google-pay.png" alt="Google Pay" />
                <img src="@/assets/jcb.png" alt="JCB" />
              </div>
            </div>
            <span class="material-icons-round ksk-ps-card__chevron">chevron_right</span>
          </div>
          <!-- Terminal offline: the card used to vanish; it now stays as a
               non-tappable card so the shopper knows why. Display only. -->
          <div
            v-else-if="allowCreditCard"
            class="ksk-ps-card ksk-ps-card--disabled ksk-ps-card--offline"
            v-bind="kskSlot('payment_method')"
          >
            <div class="ksk-ps-card__icon ksk-ps-card__icon--contactless">
              <span class="material-icons-round">contactless</span>
            </div>
            <div class="ksk-ps-card__body">
              <div class="ksk-ps-card__title-row">
                <span class="ksk-ps-card__title">{{ $t("kiosk.payment.contactless") }}</span>
                <span class="ksk-ps-card__pill ksk-ps-card__pill--error">{{ $t("kiosk.payment.terminalOffline") }}</span>
              </div>
              <div class="ksk-ps-card__desc">{{ $t("kiosk.payment.contactStaff") }}</div>
            </div>
          </div>

          <div
            v-if="allowPaynowQR"
            class="ksk-ps-card"
            v-bind="kskSlot('payment_method')"
            @click="selectPayNowType()"
          >
            <div class="ksk-ps-card__icon ksk-ps-card__icon--image ksk-ps-card__icon--logo">
              <img src="@/assets/paynow_qr.png" alt="PayNow" />
            </div>
            <div class="ksk-ps-card__body">
              <div class="ksk-ps-card__title">{{ $t("kiosk.payment.paynowQr") }}</div>
              <div class="ksk-ps-card__desc">{{ $t("kiosk.payment.paynowDesc") }}</div>
              <!-- PayNow is the big logo on the left; only the extra wallets
                   accepted through Allinpay are listed here. -->
              <div v-if="paymentMode == ALLINPAY" class="ksk-ps-card__logos">
                <img src="@/assets/alipay_qr.png" alt="Alipay" />
                <img src="@/assets/wechat_qr.png" alt="WeChat Pay" />
              </div>
            </div>
            <span class="material-icons-round ksk-ps-card__chevron">chevron_right</span>
          </div>

          <!-- Below ShopBack's $2.00 floor (startShopBackPay rejects it) the card
               is shown disabled up front instead of erroring after the tap. -->
          <div
            v-if="allowShopBack"
            class="ksk-ps-card"
            v-bind="kskSlot('payment_method')"
            :class="{ 'ksk-ps-card--disabled': isShopBackBelowMin }"
            @click="startShopBackPay()"
          >
            <div class="ksk-ps-card__icon ksk-ps-card__icon--image">
              <img src="@/assets/shopback_pay.png" alt="ShopBack Pay" />
            </div>
            <div class="ksk-ps-card__body">
              <div class="ksk-ps-card__title-row">
                <span class="ksk-ps-card__title">ShopBack Pay</span>
                <span v-if="isShopBackBelowMin" class="ksk-ps-card__pill">{{ $t("kiosk.payment.shopbackMin") }}</span>
              </div>
              <div class="ksk-ps-card__desc">{{ $t("kiosk.payment.shopbackDesc") }}</div>
            </div>
            <span class="material-icons-round ksk-ps-card__chevron">chevron_right</span>
          </div>
        </template>
        <div v-else class="ksk-pay ksk-ps__empty">
          <div class="ksk-pay__icon ksk-pay__icon--warning">
            <span class="material-icons-round">credit_card_off</span>
          </div>
          <div class="ksk-pay__title">{{ $t("kiosk.payment.notAvailable") }}</div>
          <div class="ksk-pay__caption">{{ $t("kiosk.payment.contactStaffDot") }}</div>
        </div>
      </div>
    </div>
    <!-- KSK014/KSK015: payment in progress (theme 2). Same v-if tree and the
         same copy as before; only the markup and styling changed. -->
    <div
      v-if="isLoadDone && isPaymentProcessing"
      class="ksk-pp__scroll"
      :class="{ 'ksk-pp__scroll--no-bar': !showProcessingBar }"
    >
      <!-- Same summary row as the method list, so only the part below it
           (the method list → the payment in progress) changes. -->
      <section class="ksk-ps__amount">
        <div class="ksk-ps__amount-text">
          <div class="ksk-ps__amount-label">{{ $t("kiosk.payment.amountToPay") }}</div>
          <div class="ksk-ps__amount-meta">{{ $t(itemCount == 1 ? "kiosk.common.itemOne" : "kiosk.common.itemMany", { count: itemCount }) }}</div>
        </div>
        <div class="ksk-ps__amount-value">{{ formatPrice(total) }}</div>
      </section>
      <section class="ksk-pp__card">
        <div v-if="isPaymentProcessing" class="ksk-pp__state">
          <div v-if="isShopBackPay" class="w-100">
            <ShopBackPayment
              ref="shopBackPayment"
              @paid="onShopBackPaid"
              @closed="onShopBackClosed"
              @status-change="shopBackStatus = $event"
            />
          </div>
          <!-- `!isAllinpayTimeout` rather than clearing isPaynowQR: other code
               reads that flag to decide whether a server-side cancel applies at
               all (Allinpay has none for QR orders), so it has to keep meaning
               "this order is a PayNow QR order". -->
          <!-- Shared layout with the card and ShopBack screens (.ksk-pay in
               kiosk-theme2.css): stage → title → caption → extras. -->
          <div v-else-if="isPaynowQR && !isAllinpayTimeout" class="ksk-pay">
            <!-- Still generating: status layout (icon → title → caption). -->
            <template v-if="!((paymentMode == AIRWALLEX && airwallexPayNowQrPayload) || qrImage)">
              <div class="ksk-pay__icon ksk-pay__icon--pending">
                <span class="ksk-pay__spinner"></span>
              </div>
              <div class="ksk-pay__title">{{ $t("kiosk.payment.generatingQr") }}</div>
              <div class="ksk-pay__caption">{{ $t("kiosk.payment.waitMoment") }}</div>
            </template>
            <template v-else>
              <div class="ksk-pay__title">{{ $t("kiosk.payment.scanToPay") }}</div>
              <div class="ksk-pay__caption">{{ $t("kiosk.payment.useBankingApp") }}</div>
            </template>
            <div v-if="(paymentMode == AIRWALLEX && airwallexPayNowQrPayload) || qrImage" class="ksk-pay__stage">
              <div class="ksk-pay__qr">
                <VueQRCodeComponent
                  v-if="paymentMode == AIRWALLEX && airwallexPayNowQrPayload"
                  :text="airwallexPayNowQrPayload"
                  size="480"
                  color="#222222"
                  bg-color="#ffffff"
                  error-level="M"
                  class="d-flex justify-content-center"
                />
                <img v-else :src="qrImage" alt="PayNow QR" />
              </div>
            </div>
            <template v-if="(paymentMode == AIRWALLEX && airwallexPayNowQrPayload) || qrImage">
              <div class="ksk-pay__extras">
                <img class="ksk-pay__logo" src="@/assets/banking/paynow.png" alt="paynow" />
                <img class="ksk-pay__logo" src="@/assets/banking/dbs.png" alt="dbs" />
                <img class="ksk-pay__logo" src="@/assets/banking/ocbc.png" alt="ocbc" />
                <img class="ksk-pay__logo" src="@/assets/banking/uob.png" alt="uob" />
                <img class="ksk-pay__logo" src="@/assets/banking/citi.png" alt="citi" />
                <img class="ksk-pay__logo" src="@/assets/banking/hsbc.png" alt="hsbc" />
                <img class="ksk-pay__logo" src="@/assets/banking/standard.png" alt="standard" />
                <img class="ksk-pay__logo" src="@/assets/banking/may.png" alt="may" />
                <img class="ksk-pay__logo" src="@/assets/banking/bank_of_china.png" alt="bank_of_china" />
                <img class="ksk-pay__logo" src="@/assets/banking/icbc.png" alt="icbc" />
              </div>
            </template>
          </div>
          <div v-else class="w-100">
            <div v-if="paymentStatus.status" class="ksk-pay">
              <div class="ksk-pay__title">{{ $t("kiosk.payment.tapCard") }}</div>
              <div class="ksk-pay__caption">{{ $t("kiosk.payment.followTerminal") }}</div>
              <div class="ksk-pay__stage ksk-pp__tap-art" aria-hidden="true">
                <svg viewBox="0 0 320 220">
                  <rect class="ksk-pp__tap-reader" x="70" y="120" width="180" height="84" rx="18" />
                  <g class="ksk-pp__tap-waves">
                    <path d="M150 150 a14 14 0 0 1 0 24" />
                    <path d="M160 142 a26 26 0 0 1 0 40" />
                    <path d="M170 134 a38 38 0 0 1 0 56" />
                  </g>
                  <g class="ksk-pp__tap-card">
                    <rect x="92" y="22" width="150" height="96" rx="12" transform="rotate(-12 167 70)" />
                    <rect class="ksk-pp__tap-chip" x="108" y="52" width="26" height="20" rx="4" transform="rotate(-12 167 70)" />
                  </g>
                </svg>
              </div>
              <!-- The page loader's dots would sit on the artwork, so they are
                   hidden on the in-progress screens (the overlay still blocks
                   taps) and the busy state is shown inline instead. -->
              <div class="ksk-pay__extras">
                <div v-if="loaderActive" class="ksk-pp__status">
                  <span class="ksk-pp__spinner ksk-pp__spinner--sm"></span>
                  {{ $t("kiosk.payment.connectingTerminal") }}
                </div>
                <template v-else>
                  <img class="ksk-pay__logo" src="@/assets/visa.png" alt="Visa" />
                  <img class="ksk-pay__logo" src="@/assets/card.png" alt="Mastercard" />
                  <img class="ksk-pay__logo" src="@/assets/apple-pay.png" alt="Apple Pay" />
                  <img class="ksk-pay__logo" src="@/assets/google-pay.png" alt="Google Pay" />
                  <img class="ksk-pay__logo" src="@/assets/jcb.png" alt="JCB" />
                </template>
              </div>
            </div>
            <!-- Same result layout as ShopBack's (PaymentErrorScreen). -->
            <div v-else class="ksk-pay">
              <!-- Allinpay query gave no verdict. Never says "failed": the
                   card may already have been charged. -->
              <div
                class="ksk-pay__icon"
                :class="{
                  'ksk-pay__icon--pending': paymentStatus.code == ALLINPAY_TIMEOUT_RETRY,
                  'ksk-pay__icon--warning': paymentStatus.code == ALLINPAY_TIMEOUT_FINAL,
                }"
              >
                <span v-if="paymentStatus.code == ALLINPAY_TIMEOUT_RETRY" class="ksk-pay__spinner"></span>
                <span v-else-if="paymentStatus.code == ALLINPAY_TIMEOUT_FINAL" class="material-icons-round">priority_high</span>
                <span v-else-if="paymentStatus.code == 'terminal_reader_timeout'" class="material-icons-round">timer_off</span>
                <span v-else class="material-icons-round">close</span>
              </div>
              <template v-if="isAllinpayTimeout">
                <div
                  class="ksk-pay__caption ksk-pp__allinpay-text"
                  v-html="allinpayTimeoutMessage"
                ></div>
                <div
                  v-if="paymentStatus.code == ALLINPAY_TIMEOUT_RETRY && (allinpayRetrying || timeoutRetryCountdown > 0)"
                  class="ksk-pp__allinpay-retry"
                >
                  {{
                    allinpayRetrying
                      ? $t("kiosk.payment.allinpayChecking")
                      : $t("kiosk.payment.allinpayAutoRetry", { seconds: timeoutRetryCountdown })
                  }}
                </div>
              </template>
              <template v-else>
                <div class="ksk-pay__title">
                  {{
                    $t(paymentStatus.code == "terminal_reader_timeout"
                      ? "kiosk.payment.timeOut"
                      : "kiosk.payment.failed")
                  }}
                </div>
                <div class="ksk-pay__caption">
                  {{
                    $t(paymentStatus.code == "terminal_reader_timeout"
                      ? "kiosk.payment.windowExpired"
                      : "kiosk.payment.notCompleted")
                  }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>
    </div>
    <!-- KSK014/KSK015 bottom bar while a payment is in progress. Same branch
         tree, handlers and copy as the old footer. Buttons: Retry = primary,
         going back (Other Methods) = outlined, cancel / restart = muted red. -->
    <div v-if="!showSelectionBar && showProcessingBar" class="ksk-pp__bar">
      <template v-if="isPaymentProcessing && !isShopBackPay">
        <template v-if="isPaymentCountdown">
          <div v-if="allowCancelOrder" class="ksk-pp__bar-row">
            <div class="ksk-pp__countdown">
              <KskCountdownRing :seconds="seconds" />
              <span>{{ $t("kiosk.payment.secondsLeft") }}</span>
            </div>
            <!-- KSK066: on the PayNow QR screen the same cancel reads as a way
                 back to the method list rather than cancelling the order. -->
            <KskButton
              :variant="isPaynowQR ? 'outlined' : 'muted'"
              size="md"
              :icon="isPaynowQR ? 'arrow_back' : 'close'"
              class="ksk-pp__btn"
              :class="{ 'ksk-pp__btn--danger': !isPaynowQR }"
              :disabled="isCancelling"
              @click="confirmCancelPayment()"
            >
              <span v-if="isCancelling">{{ $t("kiosk.common.waiting") + " " }}<span class="dots-animation"></span></span>
              <span v-else>{{ $t(isPaynowQR ? "kiosk.payment.otherMethods" : "kiosk.payment.cancelPayment") }}</span>
            </KskButton>
          </div>
          <template v-else>
            <div
              v-if="paymentStatus.status && !isCancelling"
              class="ksk-pp__bar-row ksk-pp__bar-row--center"
            >
              <div class="ksk-pp__countdown">
                <KskCountdownRing :seconds="seconds" />
                <span>{{ $t("kiosk.payment.secondsLeft") }}</span>
              </div>
            </div>
            <div v-else class="ksk-pp__bar-row">
              <KskButton size="md" icon="refresh" class="ksk-pp__btn" @click="retry()">
                {{ $t("kiosk.common.retry") }}
              </KskButton>
              <KskButton variant="muted" size="md" icon="close" class="ksk-pp__btn ksk-pp__btn--danger" @click="startOver()">
                {{ $t("kiosk.stillThere.cancelOrder") }}
              </KskButton>
            </div>
          </template>
        </template>

        <!-- Allinpay timeout: re-queried automatically after a 5s countdown
             (ALLINPAY_AUTO_RETRY_TIMES times). Retry skips the wait. Start Over
             only clears client state — it does not cancel a payment that may
             well have gone through. -->
        <div
          v-else-if="paymentStatus.code == ALLINPAY_TIMEOUT_RETRY"
          class="ksk-pp__bar-row"
        >
          <KskButton
            size="md"
            icon="refresh"
            class="ksk-pp__btn"
            :disabled="allinpayRetrying"
            @click="retryAllinpayQuery()"
          >
            {{ $t("kiosk.common.retry") }}
          </KskButton>
          <KskButton variant="muted" size="md" icon="restart_alt" class="ksk-pp__btn ksk-pp__btn--danger" @click="startOver()">
            {{ $t("kiosk.footer.startOver") }}
          </KskButton>
        </div>

        <!-- Retry budget spent: the only thing left is to free the kiosk. -->
        <div
          v-else-if="paymentStatus.code == ALLINPAY_TIMEOUT_FINAL"
          class="ksk-pp__bar-row"
        >
          <KskButton variant="muted" size="md" icon="restart_alt" class="ksk-pp__btn ksk-pp__btn--danger" @click="startOver()">
            {{ $t("kiosk.footer.startOver") }}
          </KskButton>
        </div>
      </template>

      <div v-else-if="isShopBackPay && shopBackStatus === 'qr'" class="ksk-pp__bar-row">
        <div class="ksk-pp__countdown">
          <KskCountdownRing :seconds="$refs.shopBackPayment?.seconds" />
          <span>{{ $t("kiosk.payment.secondsLeft") }}</span>
        </div>
        <!-- KSK066: same cancel, shown as the way back to the method list. -->
        <KskButton
          v-if="$refs.shopBackPayment?.canShowCancelButton"
          variant="outlined"
          size="md"
          icon="arrow_back"
          class="ksk-pp__btn"
          :disabled="$refs.shopBackPayment?.isCancelling"
          @click="confirmCancelShopBackPayment()"
        >
          <span v-if="$refs.shopBackPayment?.isCancelling">{{ $t("kiosk.common.waiting") + " " }}<span class="dots-animation"></span></span>
          <span v-else>{{ $t("kiosk.payment.otherMethods") }}</span>
        </KskButton>
        <!-- KSK014: shown whenever Cancel is hidden (it used to appear only once
             the shopper had scanned). Display only. -->
        <div v-else class="ksk-pp__waiting">
          <span class="ksk-pp__spinner ksk-pp__spinner--sm"></span>
          {{ $t("kiosk.payment.waitingPhone") }}
        </div>
      </div>

      <div v-else-if="isShopBackPay && shopBackStatus === 'failed'" class="ksk-pp__bar-row">
        <KskButton size="md" icon="refresh" class="ksk-pp__btn" @click="$refs.shopBackPayment.retry()">
          {{ $t("kiosk.common.retry") }}
        </KskButton>
        <KskButton variant="outlined" size="md" icon="arrow_back" class="ksk-pp__btn" @click="$refs.shopBackPayment.close()">
          {{ $t("kiosk.payment.otherMethods") }}
        </KskButton>
      </div>

      <div v-else-if="isShopBackPay && shopBackStatus === 'expired'" class="ksk-pp__bar-row">
        <KskButton variant="outlined" size="md" icon="arrow_back" class="ksk-pp__btn" @click="$refs.shopBackPayment.close()">
          {{ $t("kiosk.payment.otherMethods") }}
        </KskButton>
      </div>
    </div>
    <!-- KSK013 bottom bar: same Start Over / Order More handlers as before.
         KSK066: Start Over on the left, Order More on the right, no arrow.
         KSK087: Order More carries the same add icon as on Checkout. -->
    <div v-if="showSelectionBar" class="ksk-ps__bar">
      <KskButton
        variant="muted"
        size="md"
        icon="restart_alt"
        class="ksk-ps__bar-btn ksk-ps__start-over"
        @click="confirmStartOver()"
      >
        {{ $t("kiosk.footer.startOver") }}
      </KskButton>
      <KskButton
        v-if="!isRetry"
        variant="outlined"
        size="md"
        icon="add_circle_outline"
        class="ksk-ps__bar-btn"
        @click="orderMore()"
      >
        {{ $t("kiosk.checkout.orderMore") }}
      </KskButton>
    </div>
    <div
      class="position-fixed top-1 end-1 message-success"
      style="z-index: 99999 !important"
    >
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" :class="{ 'ksk-pp__loader--inline': isLoadDone && isPaymentProcessing }" />
    <ModalNotification
      :message="messageNotification"
      :isKiosk="true"
      :error="errorOnlyNotification"
      :closeModalNotificationHandler="closeModalNotificationHandler"
    />
    <ModalInputBuzzerNumber
      :keyReload="keyReloadModalInputBuzzerNumber"
      :inputBuzzerNumber="inputBuzzerNumber"
      :buzzerNumberText="buzzerNumberText"
    />
    <ModalConfirm
      :message="confirmModalMessage"
      :ksk-preset="kskConfirmPreset"
      :process-yes="confirmModalAction"
      :isKiosk="true"
    />
    <ModalEnterOtp @verifyOtpSuccess="verifyOtpSuccess" :total="total" :memberSignInQR="memberSignInQR" />
    <ModalSelectPayNowPaymentMethod :keyReload="keyReloadModalSelectPayNowPaymentMethod" @paynow-qr="paynowQR" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import Header from "../kiosk/components/Header.vue";
import { localStorageName } from "@/config/utils";
import { promoDiscountAmount, promoReasonText } from "@/config/promoCode";
import KioskService from "../../services/kiosk.service";
import AllinpayService from "../../services/allinpay.service";
import AirwallexService from "../../services/airwallex.service";
import ModalNotification from "@/components/ModalNotification.vue";
import ModalInputBuzzerNumber from "../kiosk/components/ModalInputBuzzerNumber.vue";
import ModalConfirm from "@/components/ModalConfirm.vue";
import $ from "jquery";
import memberService from "../../services/member.service";
import PromocodeService from "../../services/promocode.service";
import {
  FREE_ITEM,
  STRIPE,
  ALLINPAY,
  AIRWALLEX,
  ALLINPAY_TIMEOUT_RETRY,
  ALLINPAY_TIMEOUT_FINAL,
  isPromoCodeError,
} from "@/config/const";
import ModalEnterOtp from "./components/ModalEnterOtp.vue";
import OrderService from "../../services/order.service";
import ModalSelectPayNowPaymentMethod from "./components/ModalSelectPayNowPaymentMethod.vue";
import VueQRCodeComponent from "vue-qrcode-component";
import ShopBackPayment from "./components/ShopBackPayment.vue";
import KskButton from "./components/ui/KskButton.vue";
import KskCountdownRing from "./components/KskCountdownRing.vue";
import { kskSlot } from "@/config/kioskColorSlots";
import EventBus from "vue3-eventbus";
import { applyCartPrices } from "@/config/cartPrices";

// Allinpay card status poll on the terminal (cardQuery). Kept separate from the
// other gateways' 3s polls: see cardSale(). 1.5s rather than 1s so the gateway
// is asked less often while the terminal is still busy with the card.
const ALLINPAY_CARD_POLL_MS = 1500;

// Allinpay timeout screen: how many automatic re-queries, and the countdown
// before each one.
const ALLINPAY_AUTO_RETRY_TIMES = 2;
const ALLINPAY_AUTO_RETRY_SECONDS = 5;

// How long the live poll keeps going on answers with no verdict before the
// timeout screen comes up. One slow or refused query is only a missed beat.
const ALLINPAY_INCONCLUSIVE_GRACE_MS = 10000;

export default {
  name: "PaymentSelection",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    Header,
    ModalNotification,
    ModalInputBuzzerNumber,
    ModalConfirm,
    ModalEnterOtp,
    ModalSelectPayNowPaymentMethod,
    VueQRCodeComponent,
    ShopBackPayment,
    KskButton,
    KskCountdownRing,
  },
  data() {
    return {
      isLoadDone: false,
      snackbar: null,
      message: "",
      loaderActive: false,
      merchantCode: "",
      branchCode: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      memberInfo: JSON.parse(
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO"), "")
      ),
      isLogined: false,
      total: 0,
      itemSelect: [],
      reader: null,
      transaction: {},
      isPaymentProcessing: false,
      isPaymentCountdown: false,
      eWalletProcessing: false,
      paymentStatus: {
        status: true,
        code: null,
      },
      isWebhookProcessing: false,
      paymentIntent: {
        id: null,
        status: null,
        metadata: {},
      },
      intervalId: null,
      readerId: "",
      isRetry: false,
      interval: null,
      messageNotification: "",
      // Set right before showing messageNotification when the reason is a
      // stale cart (item unavailable / voucher no longer valid) — tells
      // closeModalNotificationHandler to send the shopper back to Checkout
      // to fix the cart once they acknowledge the message, instead of the
      // usual "stay on this screen" behaviour.
      redirectToCheckoutAfterNotification: false,
      // The notification modal is showing a payment error that used to be a
      // snackbar (see watch.snackbar): closing it must not run
      // closeModalNotificationHandler's follow-ups (leave the payment screen,
      // re-query, buzzer…), exactly like the snackbar it replaces.
      errorOnlyNotification: false,
      // Allinpay query-timeout recovery: up to ALLINPAY_AUTO_RETRY_TIMES
      // re-queries, each after a 5s countdown. See _showAllinpayTimeoutScreen().
      timeoutRetryCountdown: 0,
      timeoutRetryInterval: null,
      allinpayRetriesLeft: ALLINPAY_AUTO_RETRY_TIMES,
      allinpayRetrying: false,
      seconds: 50,
      intervalCountdown: null,
      intervalConnectReader: null,
      stripe: null,
      isPaynowQR: false,
      qrImage: null,
      memberQR: null,
      allowPaynowQR: false,
      allowCreditCard: false,
      allowEWallet: false,
      allowShopBack: false,
      isShopBackPay: false,
      shopBackStatus: "idle",
      buzzerNumber: "",
      keyReloadModalInputBuzzerNumber: "keyReloadModalInputBuzzerNumber",
      // Promo code variables
      promoCodeApplied: null,
      isPromoCodeApplied: false,
      showBuzzerNumber: false,
      buzzerNumberText: "",
      takeAwayOrDineIn:
        localStorage.getItem(localStorageName("DINING_OPTION")) ?? 2,
      timeoutId: null,
      allowCancelOrder: false,
      readerStatus: "online",
      enableRetailMode: 0,
      totalVoucherDiscount: 0,
      totalPromoDiscount: 0,
      staffInfo: JSON.parse(
        localStorage.getItem(localStorageName("STAFF_INFO"), "")
      ),
      staffLogined: false,
      freeItem: FREE_ITEM,
      rewardSelect: JSON.parse(
        localStorage.getItem(localStorageName("SELECTED_REWARD"), [])
      ),
      voucher_deducted_format: "",
      merchantMember: null,
      memberSignInQR: null,
      paymentMode: STRIPE,
      STRIPE,
      ALLINPAY,
      AIRWALLEX,
      ALLINPAY_TIMEOUT_RETRY,
      ALLINPAY_TIMEOUT_FINAL,
      reqId: null,
      paymentMethod: "",
      cancelOrderTimeout: null,
      isCancelling: false,
      isFetching: false,
      keyReloadModalSelectPayNowPaymentMethod: "keyReloadModalSelectPayNowPaymentMethod",
      payNowType: 1, // 1: PayNow, 2: Alipay, 3: WeChat
      // Airwallex specific state
      airwallexProcessedAt: null, // timestamp when process-payment-intent was called (grace period guard)
      airwallexMinGracePeriodMs: 3000, // disable cancel for 3s to prevent race condition
      airwallexErrorCount: 0, // consecutive non-400 errors when polling status
      airwallexMaxErrors: 3, // stop polling after this many consecutive errors
      // Airwallex PayNow: raw QR payload string returned by confirm endpoint —
      // rendered to a QR image on the fly via VueQRCodeComponent. Distinct
      // from `qrImage` which Stripe/Allinpay provide as a ready image URL.
      airwallexPayNowQrPayload: null,
      // Tracks the payment_type used when the *current* Airwallex intent was
      // created ('card' | 'pay_now'). Survives `isPaynowQR` being reset during
      // timeout/cleanup flows so cancel payloads stay correct.
      airwallexPaymentType: null,
      checkStatusFailureCount: 0,
      confirmModalMessage: "",
      kskConfirmPreset: "",
      confirmModalAction: null,
    };
  },
  async created() {
    this.confirmModalAction = this.startOver;
    this.cartName = await this.$store.dispatch("cartName");
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
    this.itemSelect = JSON.parse(localStorage.getItem(this.cartName, []));
    this.enableRetailMode =
      localStorage.getItem(localStorageName("KIOSK_MODE")) ?? 0;

    if (!this.itemSelect || this.itemSelect.length == 0) {
      if (this.enableRetailMode) {
        this.gotoPath("CheckoutPageKiosk");
      } else {
        this.gotoPath("OrderPageKiosk");
      }
    } else {
      if (this.memberInfo) {
        this.isLogined = true;
      }

      if (this.enableRetailMode && this.staffInfo) {
        this.staffLogined = true;
        await this.getStaffRemainingSpending();
      }

      this.getBranchSetting();
    }

    // Load promo code from localStorage if exists
    const savedPromoCode = localStorage.getItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
    if (savedPromoCode) {
      this.promoCodeApplied = JSON.parse(savedPromoCode);
      this.isPromoCodeApplied = true;
    }

    setTimeout(async () => {
      await this.calculateVoucherPrice();
      this.totalPromoDiscount = this.getPromoDiscountAmount();
      this.total = this.totalPayable();
    }, 100);

    if (this.isLogined || this.staffLogined) {
      await this.getHome();
    }
  },
  mounted() {
    $(".modal-backdrop.fade.show").remove();
    // Cancel any live Airwallex intent if the user refreshes / closes the tab
    // while the terminal is still waiting for a card swipe — otherwise the
    // customer can tap their card after the page has unloaded and be charged
    // while the kiosk session is gone.
    this._boundAirwallexUnloadHandler = this._airwallexUnloadCleanup.bind(this);
    window.addEventListener("beforeunload", this._boundAirwallexUnloadHandler);

    // When connectivity is regained while sitting on this screen, proactively
    // reconcile a pending intent. Guarded to avoid interrupting a live payment:
    // if the poller is still running (this.interval) or a cancel is in flight,
    // let those paths handle it instead.
    this._boundAirwallexOnlineHandler = () => {
      if (this.interval || this.isCancelling) return;
      this._reconcilePendingAirwallexOnMount();
    };
    window.addEventListener("online", this._boundAirwallexOnlineHandler);

    // KSK088: the header member chip only emits Logout; this page never
    // listened, so tapping it did nothing.
    this.busHandlers = {
      Logout: () => this.confirmLogout(),
    };
    Object.entries(this.busHandlers).forEach(([event, handler]) => EventBus.on(event, handler));
  },
  beforeUnmount() {
    Object.entries(this.busHandlers || {}).forEach(([event, handler]) => EventBus.off(event, handler));
  },
  unmounted() {
    // Read by the Allinpay timeout retry: a re-query still in flight when the
    // customer taps Start Over must not arm a new countdown on a dead page.
    this._isUnmounted = true;
    this.clearAllInterval();
    if (this._boundAirwallexUnloadHandler) {
      window.removeEventListener(
        "beforeunload",
        this._boundAirwallexUnloadHandler
      );
      this._boundAirwallexUnloadHandler = null;
    }
    if (this._boundAirwallexOnlineHandler) {
      window.removeEventListener("online", this._boundAirwallexOnlineHandler);
      this._boundAirwallexOnlineHandler = null;
    }
    // Route-change cleanup: if we leave this view while an Airwallex intent
    // is still live (e.g. user navigates back), fire a best-effort cancel.
    if (
      this.paymentMode == AIRWALLEX &&
      this.paymentIntent &&
      this.paymentIntent.id
    ) {
      AirwallexService.cancelPaymentIntent(
        this._buildAirwallexCancelPayload()
      ).catch(() => {});
    }
  },
  watch: {
    // Payment errors are shown in the notification modal (clear, needs an
    // OK) rather than a corner snackbar that is easy to miss on a kiosk.
    // Every `snackbar = "danger"` + `message` pair on this page goes through
    // here. If a notification is already open, the snackbar is kept so that
    // message is not overwritten.
    snackbar(type) {
      if (type !== "danger" || $("#modalNotification").hasClass("show")) return;
      const raw = this.message;
      this.messageNotification =
        typeof raw === "string" && raw.trim()
          ? raw
          : (raw && raw.message) || this.$t("kiosk.common.somethingWrong");
      this.errorOnlyNotification = true;
      this.snackbar = null;
      $(".btn-notification-modal").click();
    },
  },
  computed: {
    // KSK013: the old footer showed Start Over / Order More exactly when no
    // earlier branch (payment in progress, ShopBack QR/failed/expired) did.
    // KSK088: logout only from the method list, before the order is saved.
    canLogout() {
      return !this.isPaymentProcessing && !this.isRetry;
    },

    showSelectionBar() {
      return (
        !this.isPaymentProcessing &&
        !(this.isShopBackPay && ["qr", "failed", "expired"].includes(this.shopBackStatus))
      );
    },

    // Same condition the eWallet card has always used to grey itself out.
    isEWalletDisabled() {
      return (
        parseFloat(this.merchantMember?.amount_in_account) < this.total ||
        parseFloat(this.merchantMember?.amount_in_account) <= 0
      );
    },

    // Whether one of the processing bar's branches below has content; while
    // the terminal is still connecting none does, and an empty white bar
    // used to sit at the bottom.
    showProcessingBar() {
      if (this.isShopBackPay) {
        return ["qr", "failed", "expired"].includes(this.shopBackStatus);
      }
      return (
        this.isPaymentProcessing &&
        (this.isPaymentCountdown ||
          this.paymentStatus.code == ALLINPAY_TIMEOUT_RETRY ||
          this.paymentStatus.code == ALLINPAY_TIMEOUT_FINAL)
      );
    },

    // Same $2.00 floor startShopBackPay() enforces.
    isShopBackBelowMin() {
      return this.total > 0 && this.total < 2.0;
    },

    itemCount() {
      return (this.itemSelect || []).reduce(
        (sum, item) =>
          item && !item.is_delivery_fee_item ? sum + (parseInt(item.quantity) || 0) : sum,
        0
      );
    },

    // True on either Allinpay timeout screen — the one offering a re-query and
    // the dead-end one telling the customer to go to the counter.
    isAllinpayTimeout() {
      return (
        this.paymentStatus.code === ALLINPAY_TIMEOUT_RETRY ||
        this.paymentStatus.code === ALLINPAY_TIMEOUT_FINAL
      );
    },

    allinpayTimeoutMessage() {
      return this.$t(this.paymentStatus.code === ALLINPAY_TIMEOUT_FINAL
        ? "kiosk.payment.allinpayCounter"
        : "kiosk.payment.allinpayTimeout");
    },

    styleObject() {
      return {
        "--color": this.$store.state.color,
      };
    },

    totalBcrs() {
      if (this.branch.enable_bcrs != 1) return 0;

      const eachBcrsAmount = parseFloat(this.branch && this.branch.each_bcrs_amount) || 0;
      if (eachBcrsAmount <= 0) return 0;

      if (!this.itemSelect || this.itemSelect.length === 0) return 0;

      let totalQty = 0;

      this.itemSelect.forEach((item) => {
        if (!item) return;
        if (item.is_delivery_fee_item) return;

        const mainQty = parseInt(item.quantity) || 0;
        if (mainQty <= 0) return;

        // Main item
        if (parseInt(item.eligible_bcrs) === 1) {
          totalQty += mainQty;
        }

        if (item.choice) {
          // Choice item
          if (item.choice_selected && item.choice_selected.items) {
            item.choice_selected.items.forEach((itemChoice) => {
              const choiceQty = parseInt(itemChoice.qty) || 0;
              if (choiceQty <= 0) return;

              // Choice item itself
              if (
                itemChoice.item_detail &&
                parseInt(itemChoice.item_detail.eligible_bcrs) === 1
              ) {
                totalQty += choiceQty * mainQty;
              }

              // Modifier group of choice item
              if (itemChoice.item_detail && itemChoice.item_detail.modifier_group) {
                itemChoice.item_detail.modifier_group.forEach((group) => {
                  if (!group.items) return;
                  group.items.forEach((itemModifier) => {
                    const modQty = parseInt(itemModifier.quantity) || 0;

                    if (parseInt(itemModifier.eligible_bcrs) === 1) {
                      totalQty += modQty * choiceQty * mainQty;
                    }

                    // Sub modifier of modifier group of choice item
                    if (
                      itemModifier.item_in_mog &&
                      itemModifier.item_in_mog.modifier_group
                    ) {
                      itemModifier.item_in_mog.modifier_group.forEach((subGroup) => {
                        if (!subGroup.items) return;
                        subGroup.items.forEach((subItemModifier) => {
                          const subModQty = parseInt(subItemModifier.quantity) || 0;
                          if (parseInt(subItemModifier.eligible_bcrs) === 1) {
                            totalQty += subModQty * modQty * choiceQty * mainQty;
                          }
                        });
                      });
                    }
                  });
                });
              }
            });
          }
        } else {
          // Modifier group of item without choice
          if (item.modifier_group) {
            item.modifier_group.forEach((group) => {
              if (!group.items) return;
              group.items.forEach((itemModifier) => {
                const modQty = parseInt(itemModifier.quantity) || 0;

                if (parseInt(itemModifier.eligible_bcrs) === 1) {
                  totalQty += modQty * mainQty;
                }

                // Sub modifier of modifier group of item without choice
                if (
                  itemModifier.item_in_mog &&
                  itemModifier.item_in_mog.modifier_group
                ) {
                  itemModifier.item_in_mog.modifier_group.forEach((subGroup) => {
                    if (!subGroup.items) return;
                    subGroup.items.forEach((subItemModifier) => {
                      const subModQty = parseInt(subItemModifier.quantity) || 0;
                      if (parseInt(subItemModifier.eligible_bcrs) === 1) {
                        totalQty += subModQty * modQty * mainQty;
                      }
                    });
                  });
                }
              });
            });
          }
        }
      });

      return totalQty * eachBcrsAmount;
    },
  },
  methods: {
    kskSlot,

    async getHome() {
      this.showLoader();

      await KioskService.getHome(this.merchantCode).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.merchantMember = response.data?.data?.merchant_member ?? 0;
          }
        }
      );
    },
    formatPrice(price) {
      return "$ " + parseFloat(price).toFixed(2);
    },

    confirmStartOver() {
      this.closeAllModal();
      this.confirmModalMessage = this.$t("kiosk.order.confirmStartOver");
      this.kskConfirmPreset = "startOver";
      this.confirmModalAction = this.startOver;
      $(".btn-confirm-modal").click();
    },

    confirmLogout() {
      if (!this.canLogout) return;
      this.closeAllModal();
      this.confirmModalMessage = this.$t("kiosk.common.confirmLogout");
      this.kskConfirmPreset = "logout";
      this.confirmModalAction = this.logoutToCheckout;
      $(".btn-confirm-modal").click();
    },

    // KSK088: the total here was priced for the member (member price, reward,
    // promo). Back on Checkout, reloadOrder re-prices the same cart as a guest.
    async logoutToCheckout() {
      try {
        await memberService.logout(this.merchantCode);
      } catch (e) {
        // Cleared below either way; the kiosk must not stay signed in.
      }
      localStorage.removeItem(localStorageName("member"));
      localStorage.removeItem(localStorageName("KIOSK_MEMBER_INFO"));
      localStorage.removeItem(localStorageName("token"));
      localStorage.removeItem(localStorageName("STAFF_INFO"));
      localStorage.removeItem(localStorageName("SELECTED_REWARD"));
      localStorage.removeItem(localStorageName("PASSCODE_CONFIRMED"));
      localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
      this.memberInfo = null;
      this.isLogined = false;
      this.staffLogined = false;
      EventBus.emit("LogoutSuccess");
      this.gotoPath("CheckoutPageKiosk");
    },

    startOver() {
      this.closeAllModal();
      localStorage.removeItem(this.cartName);
      localStorage.removeItem(localStorageName("PAYMENT_METHOD"));
      localStorage.removeItem(localStorageName("DINING_OPTION"));
      localStorage.removeItem(localStorageName("STAFF_INFO"));
      localStorage.removeItem(localStorageName("PASSCODE_CONFIRMED"));
      localStorage.removeItem(localStorageName("SELECTED_REWARD"));
      localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
      if (this.isLogined || this.staffLogined) {
        this.logout();
      }
      this.gotoPath("LandingPageKiosk");
    },

    orderMore() {
      this.closeAllModal();

      if (this.enableRetailMode == 0) {
        this.gotoPath("OrderPageKiosk");
      } else {
        this.gotoPath("CheckoutPageKiosk");
      }
    },

    closeAllModal() {
      $(".btn-close-modal-confirm").click();
      $(".btn-hide-modal-input-buzzer-number").click();
      $(".btn-close-notification-modal").click();
    },

    inputBuzzerNumber(number) {
      if (number.length > 0) {
        $(".btn-hide-modal-input-buzzer-number").click();
      }

      this.buzzerNumber = number;
    },

    startCountdown() {
      if (this.intervalCountdown) return;

      this.intervalCountdown = setInterval(async () => {
        if (this.seconds > 0) {
          this.seconds--;

          if (this.seconds == 2) {
            if (this.paymentIntent.id) {
              if (this.paymentMode == AIRWALLEX) {
                // Airwallex: only cancel if not in grace period
                // (backend will also block if latest_attempt is in-flight)
                const elapsed = this.airwallexProcessedAt
                  ? Date.now() - this.airwallexProcessedAt
                  : Infinity;
                if (elapsed >= this.airwallexMinGracePeriodMs) {
                  await this.cancelAirwallexPaymentIntent();
                }
              } else {
                if (!this.isPaynowQR) {
                  await this.cancelReaderAction();
                }
                await this.cancelPaymentIntent();
              }
            }
          }
        } else {
          let paymentSucceeded = false;

          if (this.paymentMode == ALLINPAY && this.reqId) {
            if (this.isPaynowQR) {
              paymentSucceeded = await this.qrCodeQuery();
            } else {
              paymentSucceeded = await this.cardQuery();
            }
          } else if (this.paymentMode == AIRWALLEX && this.paymentIntent.id) {
            // Final status check in case SUCCEEDED right before timeout.
            // If it did succeed, the status handler has already navigated to
            // PaymentSuccessPageKiosk — bail before we stack a "Timeout Error"
            // modal on top of the success page.
            await this.getAirwallexPaymentIntentStatus();
            paymentSucceeded = this.paymentIntent.status === "SUCCEEDED";
          }

          // That last cardQuery() may have timed out and put the Allinpay
          // timeout screen up. Bail before the reset below tears it down and
          // stacks a "Timeout Error" modal on top of it — the payment is
          // unconfirmed, not failed.
          if (this.isAllinpayTimeout) {
            clearInterval(this.intervalCountdown);
            this.intervalCountdown = null;
            return;
          }

          clearInterval(this.intervalCountdown);
          this.intervalCountdown = null;

          if (paymentSucceeded) {
            return;
          }

          this.messageNotification = "Timeout Error";
          $(".btn-notification-modal").click();
          this.isPaynowQR = false;
          // this.reqId = null;
          this.qrImage = null;
          this.isRetry = true;
          this.isPaymentProcessing = false;
          this.isPaymentCountdown = false;
          this.allowCancelOrder = false;
          this.clearAllInterval();
          this.paymentStatus = {
            status: true,
            code: null,
          };
          this.airwallexProcessedAt = null;
          this.airwallexPayNowQrPayload = null;
        }
      }, 1000);
    },

    gotoPath(name) {
      this.$router.push({
        name: name,
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
        },
      });
    },

    selectPayNowType() {
      if (this.total > 0 && this.total < 0.50) {
        this.messageNotification = this.$t("kiosk.payment.minAmount");
        $(".btn-notification-modal").click();
        return;
      }

      if (this.paymentMode == ALLINPAY) {
        this.keyReloadModalSelectPayNowPaymentMethod = Math.random().toString();
        $(".btn-show-modal-select-pay-now-payment-method").click();
      } else {
        this.paynowQR();
      }
    },

    async paynowQR(payNowType = 1) {
      this.payNowType = payNowType;

      this.paymentMethod = "Paynow";
      // An eWallet attempt dropped at the OTP step leaves this on, and saveOrder
      // would then deduct the wallet (no OTP) and still show the QR.
      this.eWalletProcessing = false;
      this.isShopBackPay = false;

      // Double-charge guard (mirrors the card flow): reconcile any unresolved
      // Airwallex intent before starting a new PayNow payment.
      if (this.paymentMode == AIRWALLEX) {
        const canProceed = await this._canStartNewAirwallexPayment();
        if (!canProceed) return;
      }

      if (this.total == 0) {
        this.saveOrder();
      } else {
        this.isPaynowQR = true;
        this.isPaymentProcessing = true;
        this.seconds = 70;
        // Stripe/Allinpay show their QR almost instantly so we start the
        // 70s countdown (and reveal the footer) here. Airwallex needs a
        // create-intent + confirm round-trip (~2-4s) before the QR is on
        // screen — deferring isPaymentCountdown + startCountdown to
        // confirmAirwallexPaymentIntentPaynow avoids burning seconds the
        // shopper can't see AND stops the "cancelled in Xs" text from flashing
        // before the Cancel button (allowCancelOrder is set there first).
        if (this.paymentMode != AIRWALLEX) {
          this.isPaymentCountdown = true;
          this.startCountdown();
        }

        if (!this.isRetry) {
          this.saveOrder();
        } else {
          if (this.paymentMode == ALLINPAY) {
            this.getQRCodeAllinpay();
          } else if (this.paymentMode == AIRWALLEX) {
            this.createAirwallexPaymentIntentPaynow();
          } else {
            this.createPaymentIntentPaynow();
          }
        }
      }
    },

    startShopBackPay() {
      // ShopBack's In-Store API rejects orders under 200 cents outright
      // ("Invalid amount. Amount has to be at least 200.") — check the real
      // $2.00 floor here instead of the generic $0.50 used by the other
      // payment methods, so the shopper gets a clear message instead of a
      // raw API error after tapping the button.
      if (this.total > 0 && this.total < 2.0) {
        this.messageNotification = this.$t("kiosk.payment.shopbackMinAmount");
        $(".btn-notification-modal").click();
        return;
      }

      this.paymentMethod = "ShopBack Pay";
      this.isShopBackPay = true;
      this.eWalletProcessing = false;
      this.isPaynowQR = false;

      if (this.total == 0) {
        this.saveOrder();
      } else {
        this.isPaymentProcessing = true;

        if (!this.isRetry) {
          this.saveOrder();
        } else {
          this.$nextTick(() => this.startShopBackPaymentComponent());
        }
      }
    },

    startShopBackPaymentComponent() {
      // From here on, ShopBackPayment.vue owns its own "generating QR" state
      // — the global spinner must not keep spinning underneath/behind it.
      this.hideLoader();
      this.$refs.shopBackPayment?.start({
        transactionId: this.transaction.transaction_id,
        receiptNo: this.transaction.receipt_no,
        branchCode: this.branchCode,
        memberId: this.memberInfo?.id || 0,
        amount: this.total,
      });
    },

    onShopBackPaid() {
      localStorage.removeItem(this.cartName);
      localStorage.removeItem(localStorageName("DINING_OPTION"));
      localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));

      if (this.isLogined || this.staffLogined) {
        localStorage.removeItem(localStorageName("STAFF_INFO"));
        localStorage.removeItem(localStorageName("PASSCODE_CONFIRMED"));
        localStorage.removeItem(localStorageName("SELECTED_REWARD"));
        memberService.logout(this.merchantCode);
      }

      this.$router.push({
        name: "PaymentSuccessPageKiosk",
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
          transaction_id: this.transaction.transaction_id,
        },
      });
    },

    onShopBackClosed() {
      this.isShopBackPay = false;
      this.isPaymentProcessing = false;
      // Transaction already exists (saveOrder ran) — skip re-creating it if
      // the shopper picks any payment method next, same convention retry()
      // uses for the other gateways.
      this.isRetry = true;
    },

    async eWalletPayment() {
      if (this.total > 0 && this.total < 0.50) {
        this.messageNotification = this.$t("kiosk.payment.minAmount");
        $(".btn-notification-modal").click();
        return;
      }

      this.showLoader();
      var dataForm = {
        phone_number: this.memberInfo?.phone_number || "",
        phone_code: this.memberInfo?.phone_code || "",
      };
      await KioskService.sendOtpToWhatsApp(dataForm, this.merchantCode).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            this.paymentMethod = "eWallet";
            this.eWalletProcessing = true;
            // Left on by a PayNow/ShopBack attempt that failed or was retried;
            // saveOrder would open that flow for an order the wallet has paid.
            this.isPaynowQR = false;
            this.isShopBackPay = false;
            $(".btn-show-modal-enter-otp").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }

          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
      await KioskService.generateQRMemberSignIn(this.merchantCode).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.memberSignInQR = response.data.data.qr_url;
          }
        }
      );
    },

    cancelEWalletProcessing() {
      this.eWalletProcessing = false;

    },

    verifyOtpSuccess() {
      this.saveOrder();
    },

    async getBranchSetting() {
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
      };

      await KioskService.getBranchSetting(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const data = response.data.data;

            if (data.payment_mode == AIRWALLEX) {
              this.readerId = data.airwallex_terminal_id;
            } else {
              this.readerId = data.terminal_id;
            }

            this.allowCreditCard = data.allow_creditcard;
            this.allowPaynowQR = data.allow_paynow;
            this.allowEWallet = data.allow_ewallet;
            this.allowShopBack = data.allow_shopback ?? true;
            // M802: show buzzer number according to the dining option (2 = Dine In, otherwise Takeaway)
            this.showBuzzerNumber =
              this.takeAwayOrDineIn == 2
                ? data.show_buzzer_number_dine_in
                : data.show_buzzer_number_takeaway;
            this.buzzerNumberText = data.buzzer_number_text;
            this.paymentMode = data.payment_mode;
            this.isLoadDone = true;

            // Entering the payment view with terminal + payment_mode now known:
            // proactively reconcile any intent left pending by a previous
            // disconnect so the shopper doesn't have to trigger it manually.
            this._reconcilePendingAirwallexOnMount();

            if (this.showBuzzerNumber) {
              this.keyReloadModalInputBuzzerNumber = Math.random().toString();
              $(".btn-show-modal-input-buzzer-number").click();
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }

          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    verifyReaderState() {
      this.paymentMethod = "Credit Card";
      this.eWalletProcessing = false;
      this.isPaynowQR = false;
      this.isShopBackPay = false;
      if (this.total == 0) {
        this.saveOrder();
      } else {
        if (this.total > 0 && this.total < 0.50) {
          this.messageNotification = this.$t("kiosk.payment.minAmount");
          $(".btn-notification-modal").click();
          return;
        }

        if (this.paymentMode == ALLINPAY) {
          this.verifyReaderStateAllinpay();
        } else if (this.paymentMode == AIRWALLEX) {
          this.verifyReaderStateAirwallex();
        } else {
          this.verifyReaderStateStripe();
        }

        this.isPaymentProcessing = true;
      }
    },

    verifyReaderStateStripe() {
      this.isPaynowQR = false;
      $(".btn-close-modal").click();
      this.showLoader();
      var dataForm = {
        reader_id: this.readerId,
      };

      KioskService.verifyReaderStateStripe(dataForm).then(
        async (response) => {
          if (response.data.status == 200) {
            this.reader = response.data.data;
            this.readerStatus = this.reader.readerStatus;

            if (this.reader.readerStatus == "offline") {
              this.hideLoader();
              this.resetPaymentUiState(
                this.$t("kiosk.payment.terminalNotOnline")
              );
            } else {
              if (!this.isRetry) {
                this.saveOrder();
              } else {
                await this.createPaymentIntent();
              }

              this.isRetry = false;
            }
          } else {
            this.hideLoader();
            this.resetPaymentUiState(this.$t("kiosk.payment.readerNotConnected"));
          }
        },
        (error) => {
          console.log(error);
          this.hideLoader();
          this.resetPaymentUiState(this.$t("kiosk.payment.readerNotConnected"));
        }
      );
    },

    verifyReaderStateAllinpay() {
      this.isPaynowQR = false;
      $(".btn-close-modal").click();
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode
      };

      KioskService.verifyReaderStateAllinpay(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            if (response.data.data.status) {
              if (!this.isRetry) {
                this.saveOrder();
              } else {
                await this.cardSale();
              }

              this.isRetry = false;
            } else {
              this.hideLoader();
              this.resetPaymentUiState(
                this.$t("kiosk.payment.terminalNotOnline")
              );
            }
          } else {
            this.hideLoader();
            this.resetPaymentUiState(
              this.$t("kiosk.payment.terminalNotOnline")
            );
          }
        },
        (error) => {
          console.log(error);
          this.hideLoader();
          this.resetPaymentUiState(
            this.$t("kiosk.payment.terminalNotOnline")
          );
        }
      );
    },

    /* ==================== AIRWALLEX PAYMENT METHODS ==================== */

    /**
     * Verify Airwallex terminal is ACTIVE before starting payment.
     */
    async verifyReaderStateAirwallex() {
      this.isPaynowQR = false;
      $(".btn-close-modal").click();
      this.showLoader();

      // Double-charge guard: reconcile any previously dispatched-but-unresolved
      // intent (e.g. the kiosk lost connectivity while the terminal completed
      // the charge) BEFORE starting a new payment. If already paid → routed to
      // success; if still pending/unknown → user asked to wait.
      const canProceed = await this._canStartNewAirwallexPayment();
      if (!canProceed) {
        this.hideLoader();
        return;
      }

      const dataForm = {
        terminal_id: this.readerId,
      };

      AirwallexService.retrieveTerminal(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const terminal = response.data.data;

            if (terminal.status !== "ACTIVE") {
              this.messageNotification =
                this.$t("kiosk.payment.terminalNotOnline");
              $(".btn-notification-modal").click();
              this.hideLoader();
              return;
            }

            if (!this.isRetry) {
              this.saveOrder();
            } else {
              await this.createAirwallexPaymentIntent();
            }
            this.isRetry = false;
          } else {
            this.messageNotification =
              this.$t("kiosk.payment.terminalNotOnline");
            $(".btn-notification-modal").click();
            this.hideLoader();
          }
        },
        (error) => {
          console.log(error);
          this.messageNotification =
            this.$t("kiosk.payment.terminalNotOnline");
          $(".btn-notification-modal").click();
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    /**
     * Step 1 of Airwallex flow: Create Payment Intent.
     */
    async createAirwallexPaymentIntent() {
      this.showLoader();

      const dataForm = {
        amount: this.total,
        transaction_id: this.transaction.transaction_id,
        receipt_no: this.transaction.receipt_no,
        terminal_id: this.readerId,
        branch_code: this.branchCode,
        member_id: this.memberInfo?.id || 0,
        kiosk_payment_method: this.paymentMethod,
        // Explicit: card-terminal flow. Drives the backend
        // STRIPE_PAYNOW → CREDIT_CARD swap on the transaction record.
        payment_type: "card",
      };

      await AirwallexService.createPaymentIntent(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const data = response.data.data;

            this.paymentIntent = {
              id: data.payment_intent_id,
              status: true,
              metadata: {},
            };
            this.airwallexPaymentType = "card";

            this.seconds = 60;
            this.isPaymentProcessing = true;

            // Proceed to send intent to terminal. The footer (isPaymentCountdown)
            // and the Cancel button are revealed together inside
            // processAirwallexPaymentIntent once the intent actually reaches the
            // terminal — this avoids both the "cancelled in Xs" flash AND a
            // Cancel click during dispatch (which would race the in-flight
            // process call). Only a loader shows during dispatch.
            await this.processAirwallexPaymentIntent();
          } else {
            this.paymentStatus.status = false;
            this.paymentStatus.code = 500;
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }

          this.hideLoader();
        },
        (error) => {
          this.paymentStatus.status = false;
          this.paymentStatus.code = 500;
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            this.$t("kiosk.payment.createIntentFailed");
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    /**
     * Step 2 of Airwallex flow: Send intent to terminal for card swipe.
     * After this call, customer will tap/insert card on terminal.
     */
    async processAirwallexPaymentIntent() {
      this.showLoader();

      const dataForm = {
        terminal_id: this.readerId,
        payment_intent_id: this.paymentIntent.id,
      };

      await AirwallexService.processPaymentIntent(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            // Record grace period timestamp to prevent race condition on cancel
            this.airwallexProcessedAt = Date.now();
            // Reveal the footer (isPaymentCountdown) together with the Cancel
            // button so the "cancelled in Xs" text never flashes before the
            // button, and the button only becomes clickable now that the intent
            // is on the terminal.
            this.allowCancelOrder = true;
            this.isPaymentCountdown = true;
            this.airwallexErrorCount = 0;

            // Persist the dispatched intent so a connectivity loss during the
            // card swipe can be reconciled before any new payment is started.
            this._persistAirwallexIntent("card");

            clearInterval(this.intervalCountdown);
            this.intervalCountdown = null;
            this.startCountdown();

            this.paymentStatus = {
              status: true,
              code: null,
            };

            // Poll status every 3 seconds. Clear any lingering poller first
            // — otherwise a fast retry could leave two intervals running and
            // double the request rate.
            if (this.interval) {
              clearInterval(this.interval);
              this.interval = null;
            }
            this.isFetching = false;
            this.interval = setInterval(async () => {
              if (this.isFetching) return;

              this.isFetching = true;
              try {
                await this.getAirwallexPaymentIntentStatus();
              } catch (err) {
                console.error(err);
              } finally {
                this.isFetching = false;
              }
            }, 3000);
          } else {
            // this.paymentStatus.status = false;
            // this.paymentStatus.code = 500;
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            // Intent was created in step 1 but never reached the terminal:
            // cancel it server-side and reset UI to avoid an orphan intent
            // and a stuck "processing" screen.
            await this._cleanupOrphanAirwallexIntent();
          }

          this.hideLoader();
        },
        async (error) => {
          // this.paymentStatus.status = false;
          // this.paymentStatus.code = 500;
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            this.$t("kiosk.payment.terminalProcessFailed");
          await this._cleanupOrphanAirwallexIntent();
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );

      this._armCancelButtonTimeout();
    },

    /**
     * Step 3 of Airwallex flow: Poll payment intent status.
     * Terminal statuses:
     *   - REQUIRES_PAYMENT_METHOD: waiting for card
     *   - REQUIRES_CAPTURE: authorized (pre-auth), needs capture
     *   - SUCCEEDED: payment completed
     *   - CANCELLED / FAILED: failed
     */
    async getAirwallexPaymentIntentStatus() {
      if (!this.paymentIntent.id) return;

      const dataForm = {
        payment_intent_id: this.paymentIntent.id,
        terminal_id: this.readerId,
      };

      await AirwallexService.getPaymentIntentStatus(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // Reset consecutive-error counter on any successful response.
            this.airwallexErrorCount = 0;

            const data = response.data.data;
            const status = data.status;
            this.paymentIntent.status = status;

            if (status === "SUCCEEDED") {
              // Payment successful → go to success page
              this._handleAirwallexPaid(this.transaction.transaction_id);
            } else if (status === "FAILED" || status === "CANCELLED") {
              // Payment failed or cancelled (e.g. customer cancelled on terminal) → show error
              this.clearAllInterval();
              // Terminal state → the persisted intent is fully resolved.
              this._clearPersistedAirwallexIntent();
              this.paymentStatus.status = false;
              this.paymentStatus.code = 500;
              // Clear intent id for both FAILED and CANCELLED — backend won't
              // accept further actions on a terminal-state intent, so keeping
              // the id around just causes wasted cancel/reconcile round-trips
              // on Retry.
              this.paymentIntent = {
                id: null,
                status: null,
                metadata: {},
              };
              this.airwallexPaymentType = null;
            } else if (status === "REQUIRES_CAPTURE") {
              // Defensive: card-present flow expects capture_method=automatic.
              // If we land here, backend created the intent with manual capture
              // and the auth would otherwise expire silently. Stop polling and
              // surface the misconfiguration instead of waiting for timeout.
              this.clearAllInterval();
              this.paymentStatus.status = false;
              this.paymentStatus.code = 500;
              this.messageNotification =
                this.$t("kiosk.payment.configError");
              $(".btn-notification-modal").click();
            }
            // REQUIRES_PAYMENT_METHOD → continue polling
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
          }
        },
        (error) => {
          // 400 = definitive error (cancelled by terminal, etc.)
          if (error.response?.data?.result?.statusCode == 400) {
            this.messageNotification = error.response.data.result.message;
            $(".btn-notification-modal").click();
            this.clearAllInterval();
            this.paymentStatus.status = false;
            this.paymentStatus.code = 400;
          } else {
            // Stop polling after several consecutive transient errors to
            // avoid log spam and an unresponsive UI on network outages.
            this.airwallexErrorCount = (this.airwallexErrorCount || 0) + 1;
            if (this.airwallexErrorCount >= this.airwallexMaxErrors) {
              // Best-effort cancel: if the network recovers a moment later,
              // the terminal could still complete the charge and the customer
              // would be billed while the kiosk shows an error. Fire-and-
              // forget — if this also fails, the backend reconciliation /
              // Airwallex intent expiry is the final safety net.
              if (this.paymentIntent.id) {
                AirwallexService.cancelPaymentIntent(
                  this._buildAirwallexCancelPayload()
                ).catch(() => {});
              }
              this.clearAllInterval();
              this.paymentStatus.status = false;
              this.paymentStatus.code = 500;
              this.messageNotification =
                error.response?.data?.result?.message ||
                this.$t("kiosk.payment.verifyFailed");
              $(".btn-notification-modal").click();
            } else {
              this.snackbar = "danger";
              this.message =
                error.response?.data?.result?.message ||
                this.$t("kiosk.payment.statusCheckFailed");
              this.setTimeOutNotification();
            }
          }
        }
      );
    },

    /**
     * Cancel Airwallex payment intent.
     * Backend has race condition protection based on latest_payment_attempt status,
     * and we add a 3s grace period on frontend to prevent accidental cancel
     * right after the customer taps a card.
     *
     * @param {boolean} showNotification - If true, display user-facing modal for
     *   grace-period / success / error messages. Set to true only when the user
     *   explicitly clicks Cancel. For internal/auto flows (retry, countdown
     *   timeout) pass false to keep the UX silent.
     */
    async cancelAirwallexPaymentIntent(showNotification = false) {
      if (!this.paymentIntent.id) return;

      // Grace period guard: prevent cancel in first 3s after process
      if (this.airwallexProcessedAt) {
        const elapsed = Date.now() - this.airwallexProcessedAt;
        if (elapsed < this.airwallexMinGracePeriodMs) {
          if (showNotification) {
            const waitSec = Math.ceil(
              (this.airwallexMinGracePeriodMs - elapsed) / 1000
            );
            this._showNotificationModal(
              this.$t("kiosk.payment.waitBeforeCancel", { seconds: waitSec })
            );
          }
          return { blocked: true };
        }
      }

      this.showLoader();

      const dataForm = this._buildAirwallexCancelPayload();

      let result = { blocked: false, success: false };

      await AirwallexService.cancelPaymentIntent(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            result.success = true;
            this._clearPersistedAirwallexIntent();
            if (showNotification) {
              this._showNotificationModal(this.$t("kiosk.payment.cancelledSuccess"));
            }
          } else {
            // Backend blocked cancel — most commonly because the intent
            // SUCCEEDED in the brief window between the last poll and this
            // cancel request. Re-check status so the success handler can
            // route the user to the success page; otherwise surface the
            // failure to the user.
            const routed = await this._reconcileAfterFailedCancel();
            if (!routed && showNotification) {
              this._showNotificationModal(
                response.data.result.message || this.$t("kiosk.payment.cancelFailed")
              );
            }
          }

          this.hideLoader();
        },
        async (error) => {
          // Same race possibility on HTTP error — try to reconcile before
          // showing a failure message.
          const routed = await this._reconcileAfterFailedCancel();
          if (!routed && showNotification) {
            this._showNotificationModal(
              error.response?.data?.result?.message || this.$t("kiosk.payment.cancelFailed")
            );
          }
          this.hideLoader();
        }
      );

      return result;
    },

    /**
     * After a cancel request was rejected, re-check the intent status to
     * detect the race where it SUCCEEDED just before our cancel reached
     * the backend. Returns true if the status handler routed away (so the
     * caller should suppress any "Cancel failed" modal).
     */
    async _reconcileAfterFailedCancel() {
      if (!this.paymentIntent.id) return false;
      try {
        await this.getAirwallexPaymentIntentStatus();
      } catch (e) {
        return false;
      }
      return this.paymentIntent.status === "SUCCEEDED";
    },

    /**
     * Check whether the notification modal is currently visible.
     * Used to prevent opening duplicate modals on rapid repeated clicks.
     */
    _isNotificationModalOpen() {
      return $("#modalNotification").hasClass("show");
    },

    /**
     * Show the notification modal, skipping if one is already open.
     * Prevents modal stacking when user clicks Cancel multiple times quickly.
     */
    _showNotificationModal(message) {
      if (this._isNotificationModalOpen()) return;
      this.messageNotification = message;
      $(".btn-notification-modal").click();
    },

    /**
     * Synchronous best-effort cancel for the beforeunload event. The browser
     * will not wait for axios promises during unload, so we issue a raw
     * `fetch` with `keepalive: true` (which the browser is allowed to
     * complete after the page is gone). Skipped when no Airwallex intent
     * is live.
     */
    _airwallexUnloadCleanup() {
      if (this.paymentMode != AIRWALLEX) return;
      const intentId =
        this.paymentIntent && this.paymentIntent.id;
      if (!intentId) return;

      try {
        const baseUrl = process.env.VUE_APP_BASE_URL || "";
        const token = localStorage.getItem(localStorageName("token"));
        const merchantId =
          localStorage.getItem(localStorageName("MERCHANT_ID")) || "";
        const headers = {
          "Content-Type": "application/json",
          MerchantCode: this.merchantCode || "",
          MerchantId: merchantId,
        };
        if (token) headers["Authorization"] = "Bearer " + token;

        const body = {
          payment_intent_id: intentId,
          cancellation_reason: "cancelled_by_merchant",
        };
        // Only attach terminal_id for the card-terminal flow. Prefer the
        // tracked payment type over `isPaynowQR` since UI flags may have been
        // reset before unload fires.
        const isCardIntent = this.airwallexPaymentType
          ? this.airwallexPaymentType === "card"
          : !this.isPaynowQR;
        if (isCardIntent && this.readerId) {
          body.terminal_id = this.readerId;
        }

        fetch(baseUrl + "/airwallex/cancel-payment-intent", {
          method: "POST",
          headers,
          body: JSON.stringify(body),
          keepalive: true,
        });
      } catch (e) {
        // best effort — backend reconciliation / Airwallex expiry is the
        // final safety net.
      }
    },

    /**
     * Cancel an Airwallex intent that was created but never successfully
     * dispatched to the terminal (e.g. processPaymentIntent failed), then
     * reset all Airwallex-related UI state. The grace-period guard does NOT
     * apply here because the intent never reached the terminal, so there is
     * no in-flight card swipe to race against.
     */
    async _cleanupOrphanAirwallexIntent() {
      const intentId = this.paymentIntent && this.paymentIntent.id;
      // Clear the grace marker first so any concurrent cancel paths don't block.
      this.airwallexProcessedAt = null;

      if (intentId) {
        try {
          await AirwallexService.cancelPaymentIntent(
            this._buildAirwallexCancelPayload()
          );
        } catch (e) {
          // Best-effort cleanup — backend will eventually expire the intent.
          console.error("Failed to cleanup orphan Airwallex intent", e);
        }
      }

      this.paymentIntent = { id: null, status: false, metadata: {} };
      this.isPaymentProcessing = false;
      this.isPaymentCountdown = false;
      this.allowCancelOrder = false;
      this.isRetry = true;
      this.airwallexPayNowQrPayload = null;
      this.airwallexPaymentType = null;
      this._clearPersistedAirwallexIntent();
      this.clearAllInterval();
    },

    /**
     * Build the cancel-intent payload. Omits terminal_id for PayNow flows
     * (no terminal involved) — sending it would trigger a best-effort
     * terminal cancel on the backend and produce noisy
     * `terminal_busy` / `invalid_status_for_operation` warnings.
     */
    _buildAirwallexCancelPayload(reason = "cancelled_by_merchant") {
      const payload = {
        payment_intent_id: this.paymentIntent.id,
        cancellation_reason: reason,
      };
      // Use the tracked payment type, not `isPaynowQR`, because UI flags can
      // be reset by the timeout/cleanup path before this payload is built.
      const isCardIntent = this.airwallexPaymentType
        ? this.airwallexPaymentType === "card"
        : !this.isPaynowQR;
      if (isCardIntent && this.readerId) {
        payload.terminal_id = this.readerId;
      }
      return payload;
    },

    /**
     * Airwallex PayNow flow: create intent → confirm with payment_method=pay_now
     * to get QR payload → poll status (reuses getAirwallexPaymentIntentStatus
     * which already handles SUCCEEDED / FAILED / CANCELLED).
     *
     * Unlike the card-terminal flow, there is no in-flight card swipe to race
     * against, so the grace-period guard (airwallexProcessedAt) is intentionally
     * NOT set — the user may cancel at any time.
     */
    async createAirwallexPaymentIntentPaynow() {
      this.showLoader();

      // Defensive reset: if the shopper came here from a cancelled card
      // attempt, any stale paymentIntent.id must be cleared so the new
      // PayNow intent isn't shadowed.
      this.paymentIntent = { id: null, status: null, metadata: {} };
      this.airwallexErrorCount = 0;

      const dataForm = {
        amount: this.total,
        transaction_id: this.transaction.transaction_id,
        receipt_no: this.transaction.receipt_no,
        branch_code: this.branchCode,
        member_id: this.memberInfo?.id || 0,
        kiosk_payment_method: this.paymentMethod,
        // Tells backend to SKIP the STRIPE_PAYNOW → CREDIT_CARD payment_method
        // swap that the card flow performs. Without this, the transaction
        // record would be (incorrectly) labelled Credit Card.
        payment_type: "pay_now",
      };

      await AirwallexService.createPaymentIntent(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const data = response.data.data;
            this.paymentIntent = {
              id: data.payment_intent_id,
              status: true,
              metadata: {},
            };
            this.airwallexPaymentType = "pay_now";

            await this.confirmAirwallexPaymentIntentPaynow();
          } else {
            this.paymentStatus.status = false;
            this.paymentStatus.code = 500;
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.paymentStatus.status = false;
          this.paymentStatus.code = 500;
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            this.$t("kiosk.payment.createIntentFailed");
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    /**
     * Step 2 of Airwallex PayNow: confirm intent to receive the QR payload.
     * On success, render the payload via VueQRCodeComponent and start polling.
     */
    async confirmAirwallexPaymentIntentPaynow() {
      this.showLoader();

      const dataForm = {
        payment_intent_id: this.paymentIntent.id,
        shopper_name: this.memberInfo?.full_name || "",
      };

      await AirwallexService.confirmPaymentIntentPayNow(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            const data = response.data.data;
            const qrPayload = data?.next_action?.qrcode || null;

            if (!qrPayload) {
              this.snackbar = "danger";
              this.message = "PayNow QR code not available";
              this.setTimeOutNotification();
              this._cleanupOrphanAirwallexIntent();
              this.hideLoader();
              return;
            }

            this.airwallexPayNowQrPayload = qrPayload;
            this.allowCancelOrder = true;
            this._armCancelButtonTimeout();
            this.airwallexErrorCount = 0;

            // Persist the dispatched intent so a connectivity loss while the
            // shopper is paying can be reconciled before a new payment starts.
            this._persistAirwallexIntent("pay_now");

            // QR is now visible → start the 70s countdown (deferred from
            // paynowQR() so the shopper gets the full window to scan).
            this.seconds = 70;
            this.isPaymentCountdown = true;
            this.startCountdown();

            // Poll status every 3s — reuses card-flow handler which already
            // routes SUCCEEDED → success page and handles FAILED / CANCELLED.
            // Clear any lingering poller first to avoid double-rate polling
            // on fast retries.
            if (this.interval) {
              clearInterval(this.interval);
              this.interval = null;
            }
            this.isFetching = false;
            this.interval = setInterval(async () => {
              if (this.isFetching) return;
              this.isFetching = true;
              try {
                await this.getAirwallexPaymentIntentStatus();
              } catch (err) {
                console.error(err);
              } finally {
                this.isFetching = false;
              }
            }, 3000);
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            this._cleanupOrphanAirwallexIntent();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            this.$t("kiosk.payment.paynowQrFailed");
          this._cleanupOrphanAirwallexIntent();
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    /* -------- Airwallex double-charge reconciliation helpers -------- */

    /**
     * localStorage key holding the last dispatched-but-unresolved Airwallex
     * intent. Namespaced per merchant/branch so kiosks don't cross-talk.
     */
    _airwallexPendingKey() {
      return localStorageName("AIRWALLEX_PENDING_INTENT");
    },

    /**
     * Persist the in-flight intent so a connectivity loss / page reload can
     * reconcile it before the customer is allowed to pay again. Called once
     * the intent has actually been dispatched (card → terminal, or PayNow QR
     * shown).
     */
    _persistAirwallexIntent(paymentType) {
      if (!this.paymentIntent || !this.paymentIntent.id) return;
      try {
        localStorage.setItem(
          this._airwallexPendingKey(),
          JSON.stringify({
            payment_intent_id: this.paymentIntent.id,
            transaction_id: this.transaction?.transaction_id || null,
            payment_type: paymentType,
          })
        );
      } catch (e) {
        // Non-fatal: backend idempotency guard is the fallback.
      }
    },

    _getPersistedAirwallexIntent() {
      try {
        const raw = localStorage.getItem(this._airwallexPendingKey());
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },

    _clearPersistedAirwallexIntent() {
      try {
        localStorage.removeItem(this._airwallexPendingKey());
      } catch (e) {
        // ignore
      }
    },

    /**
     * Query the true status of a previously dispatched intent. Returns the raw
     * Airwallex payment-intent status (uppercase, e.g. "SUCCEEDED",
     * "REQUIRES_PAYMENT_METHOD", "CANCELLED") or "unknown" if the status could
     * not be read (still offline / soft error).
     */
    async _reconcilePendingAirwallexIntent(pending) {
      if (!pending || !pending.payment_intent_id) return "unknown";

      const dataForm = { payment_intent_id: pending.payment_intent_id };
      // terminal_id is only meaningful for the card-terminal flow.
      if (pending.payment_type === "card" && this.readerId) {
        dataForm.terminal_id = this.readerId;
      }

      let outcome = "unknown";
      await AirwallexService.getPaymentIntentStatus(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            outcome = response.data.data.status || "unknown";
          } else {
            outcome = "unknown";
          }
        },
        () => {
          outcome = "unknown";
        }
      );
      return outcome;
    },

    /**
     * Best-effort cancel of a persisted (stale) intent + its terminal
     * operation. Returns true only if Airwallex confirmed the cancel.
     */
    async _cancelPendingAirwallexIntent(pending) {
      const payload = {
        payment_intent_id: pending.payment_intent_id,
        cancellation_reason: "cancelled_by_merchant",
      };
      if (pending.payment_type === "card" && this.readerId) {
        payload.terminal_id = this.readerId;
      }

      let cancelled = false;
      await AirwallexService.cancelPaymentIntent(payload).then(
        (r) => {
          cancelled = !!r.data?.result?.isSuccess;
        },
        () => {
          cancelled = false;
        }
      );
      return cancelled;
    },

    /**
     * Block the caller from starting a new payment while the previous one is
     * genuinely in-flight or its status is unknown (still offline).
     */
    _blockNewAirwallexPayment() {
      this.hideLoader();
      this.isPaymentProcessing = false;
      this.messageNotification =
        this.$t("kiosk.payment.stillConfirming");
      $(".btn-notification-modal").click();
    },

    /**
     * Guard invoked BEFORE starting any new Airwallex payment. Reconciles a
     * previously unresolved intent to (a) close the double-charge window where
     * the terminal completed the charge while the kiosk was offline, and (b)
     * release a stale intent that was never actually paid so the shopper isn't
     * stuck.
     *
     * @returns {Promise<boolean>} true if it is safe to proceed with a NEW
     *   payment; false if the caller must stop (already paid → routed to
     *   success, or still in-flight / unknown → user asked to wait).
     */
    async _canStartNewAirwallexPayment() {
      const pending = this._getPersistedAirwallexIntent();
      if (!pending || !pending.payment_intent_id) return true;

      const status = await this._reconcilePendingAirwallexIntent(pending);

      // Already paid → success page, never charge twice.
      if (status === "SUCCEEDED") {
        this._handleAirwallexPaid(pending.transaction_id);
        return false;
      }

      // Definitively finished without a charge → safe to start a new payment.
      if (
        status === "CANCELLED" ||
        status === "FAILED" ||
        status === "EXPIRED"
      ) {
        this._clearPersistedAirwallexIntent();
        return true;
      }

      // No card was ever captured (kiosk went offline and the shopper never
      // paid). No money moved → cancel the stale intent + terminal operation
      // and let the shopper pay fresh instead of blocking them.
      if (status === "REQUIRES_PAYMENT_METHOD") {
        const cancelled = await this._cancelPendingAirwallexIntent(pending);
        if (cancelled) {
          this._clearPersistedAirwallexIntent();
          return true;
        }

        // Cancel rejected → the shopper may have tapped in the race window.
        // Re-check before deciding.
        const recheck = await this._reconcilePendingAirwallexIntent(pending);
        if (recheck === "SUCCEEDED") {
          this._handleAirwallexPaid(pending.transaction_id);
          return false;
        }
        if (
          recheck === "CANCELLED" ||
          recheck === "FAILED" ||
          recheck === "EXPIRED"
        ) {
          this._clearPersistedAirwallexIntent();
          return true;
        }
        // Still ambiguous → fall through to block.
      }

      // Genuinely in-flight (PENDING / REQUIRES_CAPTURE / REQUIRES_CUSTOMER_ACTION)
      // or status unknown (still offline) → do NOT open a second payment.
      this._blockNewAirwallexPayment();
      return false;
    },

    /**
     * Finalize a paid Airwallex order: clear local state and route to the
     * kiosk success page. Shared by the live poll (SUCCEEDED) and the
     * reconciliation guard.
     */
    _handleAirwallexPaid(transactionId) {
      this._clearPersistedAirwallexIntent();
      localStorage.removeItem(this.cartName);
      this.clearAllInterval();
      localStorage.removeItem(localStorageName("DINING_OPTION"));
      localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));

      if (this.isLogined || this.staffLogined) {
        localStorage.removeItem(localStorageName("STAFF_INFO"));
        localStorage.removeItem(localStorageName("PASSCODE_CONFIRMED"));
        localStorage.removeItem(localStorageName("SELECTED_REWARD"));
        memberService.logout(this.merchantCode);
      }

      this.$router.push({
        name: "PaymentSuccessPageKiosk",
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
          transaction_id: transactionId,
        },
      });
    },

    /**
     * Proactive reconciliation run when the kiosk (re)enters this view or comes
     * back online — cleans up a pending intent WITHOUT waiting for the shopper
     * to tap a button. Silent by design: no "please wait" modal, and it only
     * acts when the outcome is unambiguous.
     *
     *  - SUCCEEDED                    → route to success (shopper already paid)
     *  - CANCELLED/FAILED/EXPIRED     → clear the stale record
     *  - REQUIRES_PAYMENT_METHOD      → no charge happened → cancel intent +
     *                                   terminal op, clear on confirmed cancel
     *  - in-flight / unknown          → leave as-is; the pre-payment guard (or
     *                                   the live poller) will handle it
     */
    async _reconcilePendingAirwallexOnMount() {
      if (this.paymentMode != AIRWALLEX) return;
      if (this._airwallexReconciling) return;

      const pending = this._getPersistedAirwallexIntent();
      if (!pending || !pending.payment_intent_id) return;

      this._airwallexReconciling = true;
      try {
        const status = await this._reconcilePendingAirwallexIntent(pending);

        if (status === "SUCCEEDED") {
          this._handleAirwallexPaid(pending.transaction_id);
          return;
        }

        if (
          status === "CANCELLED" ||
          status === "FAILED" ||
          status === "EXPIRED"
        ) {
          this._clearPersistedAirwallexIntent();
          return;
        }

        if (status === "REQUIRES_PAYMENT_METHOD") {
          const cancelled = await this._cancelPendingAirwallexIntent(pending);
          if (cancelled) {
            this._clearPersistedAirwallexIntent();
          }
          return;
        }
        // in-flight / unknown → leave the pending record for later reconcile.
      } finally {
        this._airwallexReconciling = false;
      }
    },

    /* ==================== END AIRWALLEX ==================== */

    async cardSale() {
      this.showLoader();
      var dataForm = {
        amount: this.total,
        transaction_id: this.transaction.transaction_id,
        receipt_no: this.transaction.receipt_no,
        branch_code: this.branchCode,
        member_id: this.memberInfo?.id || 0,
        kiosk_payment_method: this.paymentMethod
      };

      await AllinpayService.cardSale(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // Fresh attempt → the customer gets their retries back.
            this.allinpayRetriesLeft = ALLINPAY_AUTO_RETRY_TIMES;
            this._allinpayInconclusiveSince = null;
            this.isPaymentProcessing = true;
            this.isPaymentCountdown = true;
            this.reqId = response.data.data.reqId;
            this.allowCancelOrder = true;
            this.seconds = 70;
            clearInterval(this.intervalCountdown);
            this.intervalCountdown = null;
            this.startCountdown();

            this.paymentStatus = {
              status: true,
              code: null,
            };

            // Every 1.5s, not every 3: the bill prints only once the order is
            // known to be paid, so this interval is directly part of the wait
            // between the tap and the bill. isFetching keeps it at one query in
            // flight, so a slow answer stretches the cycle instead of stacking.
            this.isFetching = false;
            this.interval = setInterval(async () => {
              if (this.isFetching) {
                return;
              }

              this.isFetching = true;

              try {
                await this.cardQuery();
              } catch (err) {
                console.error(err);
              } finally {
                this.isFetching = false;
              }
            }, ALLINPAY_CARD_POLL_MS);
          } else {
            this.handlePaymentInitError(response.data.result.message);
          }

          this.hideLoader();
        },
        (error) => {
          this.hideLoader();
          this.handlePaymentInitError(error.response?.data?.result?.message);
        }
      );

      this._armCancelButtonTimeout();
    },

    async cardQuery() {
      // "paid" | "failed" | "timeout" | "error" | "pending" — read by retryAllinpayQuery()
      // to decide what to show once the one manual re-query comes back. Set
      // before the reqId guard: a cleared reqId is "we never found out", not
      // whatever the previous call happened to conclude.
      this._lastCardQueryOutcome = "pending";

      if (!this.reqId) {
        return false;
      }

      // this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        transaction_id: this.reqId
      };
      let succeeded = false;

      await AllinpayService.cardQuery(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // The terminal gave no verdict. The card may already be charged, so
            // never fail the order here — show the timeout screen instead.
            if (response.data.data.error_code === "TIMEOUT") {
              this._lastCardQueryOutcome = "timeout";
              this._onAllinpayInconclusive();
              return;
            }

            this._allinpayInconclusiveSince = null;

            if (response.data.data.status && response.data.data.payment_status) {
              succeeded = true;
              this._lastCardQueryOutcome = "paid";
              localStorage.removeItem(this.cartName);
              clearInterval(this.interval);
              this.interval = null;
              clearInterval(this.intervalCountdown);
              this.intervalCountdown = null;
              localStorage.removeItem(localStorageName("DINING_OPTION"));
              // Clear promo code after successful payment
              localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
              if (this.isLogined || this.staffLogined) {
                localStorage.removeItem(localStorageName("STAFF_INFO"));
                localStorage.removeItem(
                  localStorageName("PASSCODE_CONFIRMED")
                );
                localStorage.removeItem(localStorageName("SELECTED_REWARD"));
                memberService.logout(this.merchantCode);
              }

              this.$router.push({
                name: "PaymentSuccessPageKiosk",
                params: {
                  merchant_code: this.merchantCode,
                  branch_code: this.branchCode,
                  transaction_id: this.transaction.transaction_id,
                },
              });
            } else if (!response.data.data.status && !response.data.data.payment_status) {
              this._lastCardQueryOutcome = "failed";
              clearInterval(this.interval);
              this.interval = null;
              clearInterval(this.intervalCountdown);
              this.intervalCountdown = null;
              this.paymentStatus.status = false;
              this.paymentStatus.code = 500;
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
          }

          // this.hideLoader();
        },
        (error) => {
          // No response at all (network/gateway timeout) or a 5xx is just as
          // inconclusive as a terminal timeout — same treatment, never a fail.
          const status = error.response?.status;
          if (!error.response || status >= 500) {
            this._lastCardQueryOutcome = "timeout";
            this._onAllinpayInconclusive();
            return;
          }

          this._lastCardQueryOutcome = "error";
          this.clearAllInterval();

          this.messageNotification =
            error.response?.data?.result?.message ||
            this.$t("kiosk.common.somethingWrong");
          $(".btn-notification-modal").click();
          // this.hideLoader();
        }
      );

      return succeeded;
    },

    /**
     * A card / QR query came back with no verdict. While the live poll is
     * still running with time left on the countdown, keep polling and raise
     * the timeout screen only once nothing conclusive has come back for
     * ALLINPAY_INCONCLUSIVE_GRACE_MS. The countdown's last query and a re-query
     * from the timeout screen run with no poll (or no time) left, so they raise
     * it straight away, as before.
     */
    _onAllinpayInconclusive(flow = "card") {
      if (this.interval && this.seconds > 0) {
        const now = Date.now();
        if (!this._allinpayInconclusiveSince) {
          this._allinpayInconclusiveSince = now;
        }
        if (now - this._allinpayInconclusiveSince < ALLINPAY_INCONCLUSIVE_GRACE_MS) {
          return;
        }
      }

      this._allinpayInconclusiveSince = null;
      this._showAllinpayTimeoutScreen(flow);
    },

    /**
     * Show the "we could not confirm your payment" screen, in place of the
     * tap-to-pay one — not as a modal, since the customer must keep reading it.
     *
     * While retries are left: count down 5s (so the terminal has a chance to
     * settle) and re-query on its own; Retry skips the wait. Once every retry
     * also came back inconclusive: send the customer to the counter with the
     * payment record on their phone, since we genuinely cannot tell from here
     * whether they were charged.
     *
     * isPaymentCountdown stays false so the footer offers neither "Cancel
     * Payment" nor the generic Retry — the latter runs cancelCardSale() and
     * starts a fresh charge, which is the last thing an unconfirmed payment
     * needs.
     *
     * `flow` records which poll raised the screen so Retry re-queries the same
     * one. Nothing else about the payment state is touched — in particular
     * isPaynowQR stays as it was, see the template comment.
     */
    _showAllinpayTimeoutScreen(flow = "card") {
      this.clearAllInterval();
      this._timeoutFlow = flow;
      this.isPaymentProcessing = true;
      this.isPaymentCountdown = false;
      this.allowCancelOrder = false;
      this.paymentStatus = {
        status: false,
        code: this.allinpayRetriesLeft > 0
          ? ALLINPAY_TIMEOUT_RETRY
          : ALLINPAY_TIMEOUT_FINAL,
      };

      // Not while a re-query is in flight: retryAllinpayQuery() decides what
      // comes next once it returns.
      if (this.allinpayRetriesLeft > 0 && !this.allinpayRetrying) {
        this._startTimeoutRetryCountdown();
      }
    },

    _startTimeoutRetryCountdown(seconds = ALLINPAY_AUTO_RETRY_SECONDS) {
      if (this._isUnmounted) {
        return;
      }

      clearInterval(this.timeoutRetryInterval);
      this.timeoutRetryCountdown = seconds;
      this.timeoutRetryInterval = setInterval(() => {
        if (this.timeoutRetryCountdown > 1) {
          this.timeoutRetryCountdown--;
          return;
        }

        clearInterval(this.timeoutRetryInterval);
        this.timeoutRetryInterval = null;
        this.timeoutRetryCountdown = 0;
        this.retryAllinpayQuery();
      }, 1000);
    },

    /**
     * One re-query, from the countdown or the Retry button. Spends a retry
     * up-front so the last inconclusive answer lands on the counter message,
     * not another countdown.
     */
    async retryAllinpayQuery() {
      if (
        this._isUnmounted ||
        this.allinpayRetriesLeft <= 0 ||
        this.allinpayRetrying ||
        this.paymentStatus.code !== ALLINPAY_TIMEOUT_RETRY
      ) {
        return;
      }

      this.allinpayRetriesLeft--;
      this.allinpayRetrying = true;
      clearInterval(this.timeoutRetryInterval);
      this.timeoutRetryInterval = null;
      this.timeoutRetryCountdown = 0;

      this.showLoader();
      try {
        if (this._timeoutFlow === "qr") {
          await this.qrCodeQuery();
        } else {
          await this.cardQuery();
        }
      } catch (err) {
        console.error(err);
        this._lastCardQueryOutcome = "timeout";
      } finally {
        this.allinpayRetrying = false;
        this.hideLoader();
      }

      if (this._isUnmounted) {
        return;
      }

      // A 4xx already put its error up: no more automatic re-queries behind
      // it — straight to the counter message, as before auto-retry.
      if (this._lastCardQueryOutcome === "error") {
        this.allinpayRetriesLeft = 0;
      }

      // "paid" already routed to the success page. The rest still need a
      // verdict on screen.
      if (this._lastCardQueryOutcome === "failed" && this._timeoutFlow !== "qr") {
        // A real decline — hand back the normal failed screen, footer buttons
        // and all, so the customer can retry or cancel the order. Card only:
        // the QR poll has no decline verdict, and its footer buttons would run
        // cancelCardSale() on an order that has no card side to cancel.
        this.isPaymentCountdown = true;
      } else if (this._lastCardQueryOutcome !== "paid") {
        // Timed out again, or answered but would not commit either way: the
        // next countdown, or the counter message once no retry is left. Same
        // flow, so the screen does not silently switch to the other one.
        this._showAllinpayTimeoutScreen(this._timeoutFlow);
      }
    },

    async cancelCardSale() {
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        transaction_id: this.reqId
      };
      let succeeded = false;

      await AllinpayService.cancelCardSale(dataForm).then(
        (response) => {
          this.hideLoader();

          if (response.data.result.isSuccess) {
            succeeded = true;
          } else {
            this.resetPaymentUiState(response.data.result.message);
          }
        },
        (error) => {
          this.hideLoader();
          this.resetPaymentUiState(error.response?.data?.result?.message);
        }
      );

      return succeeded;
    },

    async createPaymentIntent() {
      this.showLoader();
      var dataForm = {
        amount: this.total,
        transaction_id: this.transaction.transaction_id,
        receipt_no: this.transaction.receipt_no,
        reader_id: this.readerId,
        branch_code: this.branchCode,
        member_id: this.memberInfo?.id || 0,
        kiosk_payment_method: this.paymentMethod
      };

      await KioskService.createPaymentIntent(dataForm).then(
        (response) => {
          this.isPaymentProcessing = true;

          if (response.data.status == 200) {
            this.seconds = 60;
            this.isPaymentCountdown = true;
            const paymentIntent = response.data.data;
            this.paymentIntent = {
              id: paymentIntent.id,
              status: true,
              metadata: {},
            };

            setTimeout(() => {
              this.requestPaymentReader();
            }, 100);
          } else {
            this.handlePaymentInitError();
          }

          this.hideLoader();
        },
        (error) => {
          this.hideLoader();
          this.handlePaymentInitError(error.response?.data?.result?.message);
        }
      );
    },

    async createPaymentIntentPaynow() {
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        amount: this.total,
        transaction_id: this.transaction.transaction_id,
        receipt_no: this.transaction.receipt_no,
        member_id: this.memberInfo?.id || 0,
        kiosk_payment_method: this.paymentMethod
      };

      await KioskService.createPaymentIntentPaynow(dataForm).then(
        (response) => {
          if (response.data.status == 200) {
            const paymentIntent = response.data.data;

            this.paymentIntent = {
              id: paymentIntent.id,
              status: true,
              metadata: {},
            };

            setTimeout(() => {
              this.processPaymentPaynow();
            }, 100);
          } else {
            this.handlePaymentInitError();
          }

          this.hideLoader();
        },
        (error) => {
          this.hideLoader();
          this.handlePaymentInitError(error.response?.data?.result?.message);
        }
      );
    },

    async getQRCodeAllinpay() {
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        amount: this.total,
        transaction_id: this.transaction.transaction_id,
        receipt_no: this.transaction.receipt_no,
        member_id: this.memberInfo?.id || 0,
        kiosk_payment_method: this.paymentMethod,
        type: this.payNowType,
      };

      await AllinpayService.getQRCode(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // Fresh attempt → the shopper gets their retries back, same as
            // cardSale() does for the card flow.
            this.allinpayRetriesLeft = ALLINPAY_AUTO_RETRY_TIMES;
            this._allinpayInconclusiveSince = null;
            this.qrImage = response.data.data.qrCode;
            this.reqId = response.data.data.access_order_id;
            this.allowCancelOrder = true;
            this._armCancelButtonTimeout();

            // Mirrors the card poll: one query in flight at a time. Without the
            // guard a slow gateway stacks a new request every 3s on top of the
            // ones still hanging.
            this.isFetching = false;
            this.interval = setInterval(async () => {
              if (this.isFetching) {
                return;
              }

              this.isFetching = true;

              try {
                await this.qrCodeQuery();
              } catch (err) {
                console.error(err);
              } finally {
                this.isFetching = false;
              }
            }, 3000);
          } else {
            this.handlePaymentInitError(response.data.result.message);
          }

          this.hideLoader();
        },
        (error) => {
          this.hideLoader();
          this.handlePaymentInitError(error.response?.data?.result?.message);
        }
      );
    },

    async qrCodeQuery() {
      // Same bookkeeping as cardQuery(): read by retryAllinpayQuery() to decide
      // what to show once the one manual re-query comes back. Set before the
      // reqId guard so a cleared reqId reads as "we never found out".
      this._lastCardQueryOutcome = "pending";

      if (!this.reqId) {
        return false;
      }

      // this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        transaction_id: this.reqId,
      };
      let succeeded = false;

      await AllinpayService.qrCodeQuery(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            // The gateway gave no verdict. The shopper may already have paid on
            // their phone, so never tear the order down here.
            if (response.data.data.error_code === "TIMEOUT") {
              this._lastCardQueryOutcome = "timeout";
              this._onAllinpayInconclusive("qr");
              return;
            }

            this._allinpayInconclusiveSince = null;

            if (response.data.data.status) {
              succeeded = true;
              this._lastCardQueryOutcome = "paid";
              localStorage.removeItem(this.cartName);
              clearInterval(this.interval);
              this.interval = null;
              clearInterval(this.intervalCountdown);
              this.intervalCountdown = null;
              localStorage.removeItem(localStorageName("DINING_OPTION"));
              // Clear promo code after successful payment
              localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
              if (this.isLogined || this.staffLogined) {
                localStorage.removeItem(localStorageName("STAFF_INFO"));
                localStorage.removeItem(
                  localStorageName("PASSCODE_CONFIRMED")
                );
                localStorage.removeItem(localStorageName("SELECTED_REWARD"));
                memberService.logout(this.merchantCode);
              }

              this.$router.push({
                name: "PaymentSuccessPageKiosk",
                params: {
                  merchant_code: this.merchantCode,
                  branch_code: this.branchCode,
                  transaction_id: this.transaction.transaction_id,
                },
              });
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
          }

          // this.hideLoader();
        },
        (error) => {
          // No response at all (network/gateway timeout) or a 5xx is just as
          // inconclusive as a gateway timeout — same treatment, never a fail.
          const status = error.response?.status;
          if (!error.response || status >= 500) {
            this._lastCardQueryOutcome = "timeout";
            this._onAllinpayInconclusive("qr");
            return;
          }

          this._lastCardQueryOutcome = "error";

          this.snackbar = "danger";
          this.message = error.response?.data?.result?.message;
          // this.hideLoader();
          this.setTimeOutNotification();
        }
      );

      return succeeded;
    },

    async processPaymentPaynow() {
      this.showLoader();
      var dataForm = {
        payment_intent_id: this.paymentIntent.id,
      };

      await KioskService.processPaymentPaynow(dataForm).then(
        (response) => {
          if (response.data.status == 200) {
            const data = response.data.data;

            if (data.next_action) {
              this.allowCancelOrder = true;
              this._armCancelButtonTimeout();
              this.qrImage =
                data.next_action.paynow_display_qr_code.image_url_png;

              this.checkStatusFailureCount = 0;
              this.isFetching = false;
              this.interval = setInterval(async () => {
                if (this.isFetching) return;

                this.isFetching = true;
                try {
                  await this.checkPaymentIntentStatus();
                } catch (err) {
                  console.error(err);
                } finally {
                  this.isFetching = false;
                }
              }, 3000);
            }
          } else {
            this.handlePaymentInitError();
          }

          this.hideLoader();
        },
        (error) => {
          this.hideLoader();
          this.handlePaymentInitError(error.response?.data?.result?.message);
        }
      );
    },

    async eWalletPaymentSuccess() {
      this.showLoader();

      localStorage.removeItem(this.cartName);
      clearInterval(this.interval);
      this.interval = null;
      clearInterval(this.intervalCountdown);
      this.intervalCountdown = null;
      localStorage.removeItem(localStorageName("DINING_OPTION"));
      // Clear promo code after successful payment
      localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
      if (this.isLogined || this.staffLogined) {
        localStorage.removeItem(localStorageName("STAFF_INFO"));
        localStorage.removeItem(
          localStorageName("PASSCODE_CONFIRMED")
        );
        localStorage.removeItem(localStorageName("SELECTED_REWARD"));
        memberService.logout(this.merchantCode);
      }

      this.$router.push({
        name: "PaymentSuccessPageKiosk",
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
          transaction_id: this.transaction.transaction_id,
        },
      });

      this.hideLoader();
    },

    async requestPaymentReader() {
      this.showLoader();
      var dataForm = {
        reader_id: this.readerId,
        payment_intent_id: this.paymentIntent.id,
      };

      await KioskService.requestPaymentReader(dataForm).then(
        (response) => {
          if (response.data.status == 200) {
            this.allowCancelOrder = true;
            this._armCancelButtonTimeout();
            clearInterval(this.intervalCountdown);
            this.intervalCountdown = null;
            this.startCountdown();

            this.checkStatusFailureCount = 0;
            this.isFetching = false;
            this.interval = setInterval(async () => {
              if (this.isFetching) return;

              this.isFetching = true;
              try {
                await this.checkPaymentIntentStatus();
              } catch (err) {
                console.error(err);
              } finally {
                this.isFetching = false;
              }
            }, 3000);
          } else {
            if (this.paymentIntent.id) {
              this.cancelPaymentIntent();
              this.cancelReaderAction();
            }
            this.handlePaymentInitError();
          }

          this.hideLoader();
        },
        (error) => {
          this.hideLoader();
          this.handlePaymentInitError(error.response?.data?.result?.message);
        }
      );
    },

    async cancelPaymentIntent() {
      this.showLoader();
      var dataForm = {
        payment_intent_id: this.paymentIntent.id,
      };

      await KioskService.cancelPaymentIntent(dataForm).then(
        async (response) => {
          if (response.data.status == 200) {
            console.log("success");
          } else {
            this.snackbar = "danger";
            this.message = response.message;
            this.setTimeOutNotification();
          }

          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    async cancelReaderAction() {
      this.showLoader();
      var dataForm = {
        reader_id: this.readerId,
        payment_intent_id: this.paymentIntent.id,
      };

      await KioskService.cancelReaderAction(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            console.log("success");
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }

          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    // Reset the payment screen back to the method-selection state and surface
    // `message` via the notification modal. Used for failures that happen
    // before a payment attempt has produced anything worth preserving.
    resetPaymentUiState(message) {
      this.checkStatusFailureCount = 0;
      this.clearAllInterval();
      clearTimeout(this.cancelOrderTimeout);
      this.cancelOrderTimeout = null;
      this.allowCancelOrder = false;
      this.paymentStatus = {
        status: true,
        code: null,
      };
      this.messageNotification = message || this.$t("kiosk.common.somethingWrong");
      $(".btn-notification-modal").click();
    },

    // Same as resetPaymentUiState, but also marks the attempt as a retry —
    // use this when the order/transaction already exists server-side so the
    // next attempt reuses it instead of calling saveOrder() again.
    handlePaymentInitError(message) {
      this.isRetry = true;
      this.resetPaymentUiState(message);
    },

    // Stripe status-polling can hit transient errors; only give up (and stop
    // polling) after a few consecutive failures instead of spamming a toast
    // every 3s.
    handleCheckStatusFailure(message) {
      this.checkStatusFailureCount++;

      if (this.checkStatusFailureCount < 3) {
        return;
      }

      this.handlePaymentInitError(
        message || this.$t("kiosk.payment.checkStatusFailed")
      );
    },

    confirmCancelPayment() {
      this.closeAllModal();
      this.confirmModalMessage = this.$t("kiosk.payment.confirmCancelPayment");
      this.kskConfirmPreset = "cancelPayment";
      this.confirmModalAction = this.cancelPayment;
      $(".btn-confirm-modal").click();
    },

    async cancelPayment() {
      if (this.isCancelling) return;
      this.closeAllModal();

      // Airwallex: check grace period FIRST before any state change
      // to prevent race condition with in-flight card swipe.
      if (this.paymentMode == AIRWALLEX && this.airwallexProcessedAt) {
        const elapsed = Date.now() - this.airwallexProcessedAt;
        if (elapsed < this.airwallexMinGracePeriodMs) {
          const waitSec = Math.ceil(
            (this.airwallexMinGracePeriodMs - elapsed) / 1000
          );
          this.snackbar = "danger";
          this.message = this.$t("kiosk.payment.waitBeforeCancel", { seconds: waitSec });
          this.setTimeOutNotification();
          return;
        }
      }

      this.showLoader();
      this.isCancelling = true;

      if (this.paymentMode == ALLINPAY) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      this.hideLoader();
      this.isRetry = true;
      this.isPaymentProcessing = false;
      this.isPaymentCountdown = false;
      this.clearAllInterval();
      this.paymentStatus = {
        status: true,
        code: null,
      };
      this.allowCancelOrder = false;
      clearInterval(this.intervalCountdown);
      this.intervalCountdown = null;
      clearTimeout(this.cancelOrderTimeout);
      this.cancelOrderTimeout = null;

      let showGenericSuccessModal = true;

      if (this.paymentMode == ALLINPAY) {
        if (this.reqId) {
          showGenericSuccessModal = await this.cancelCardSale();
        }
      } else if (this.paymentMode == AIRWALLEX) {
        // cancelAirwallexPaymentIntent(true) already shows its own
        // success/failure modal — don't stack a generic one on top.
        showGenericSuccessModal = false;

        if (this.paymentIntent.id) {
          // User explicitly clicked Cancel → show feedback modal
          const res = await this.cancelAirwallexPaymentIntent(true);
          // Only clear the grace-period marker if cancel wasn't blocked.
          // If blocked, keep airwallexProcessedAt so next cancel attempt
          // still enforces the grace window.
          if (!res || !res.blocked) {
            this.airwallexProcessedAt = null;
          }
        } else {
          this.airwallexProcessedAt = null;
        }
      } else {
        if (this.paymentIntent.id) {
          if (!this.isPaynowQR) {
            await this.cancelReaderAction();
          }
          await this.cancelPaymentIntent();
        }
      }

      this.isPaynowQR = false;
      this.qrImage = null;
      this.airwallexPayNowQrPayload = null;
      this.isCancelling = false;

      if (showGenericSuccessModal) {
        this.messageNotification = this.$t("kiosk.payment.paymentCancelled");
        $(".btn-notification-modal").click();
      }
    },

    confirmCancelShopBackPayment() {
      this.closeAllModal();
      this.confirmModalMessage = this.$t("kiosk.payment.confirmCancelPayment");
      this.kskConfirmPreset = "cancelPayment";
      this.confirmModalAction = this.cancelShopBackPayment;
      $(".btn-confirm-modal").click();
    },

    async cancelShopBackPayment() {
      this.closeAllModal();
      const cancelled = await this.$refs.shopBackPayment?.cancelManually();

      if (cancelled) {
        this.messageNotification = this.$t("kiosk.payment.paymentCancelled");
        $(".btn-notification-modal").click();
      }
    },

    async checkPaymentIntentStatus() {
      var dataForm = {
        reader_id: this.readerId,
        payment_intent_id: this.paymentIntent.id,
        transaction_id: this.transaction.transaction_id,
        is_paynowQR: this.isPaynowQR,
      };

      await KioskService.checkPaymentIntentStatus(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.checkStatusFailureCount = 0;
            this.reader = response.data.data.reader;

            if (
              this.reader &&
              this.reader.status == "offline" &&
              !this.isPaynowQR
            ) {
              this.readerStatus = "offline";
              this.retry();
              this.messageNotification =
                this.$t("kiosk.payment.terminalNotOnline");
              $(".btn-notification-modal").click();

              // this.intervalConnectReader = setInterval(() => {
              //   this.verifyReaderState();
              // }, 30000);
            } else {
              const status = response.data.data.status;
              this.paymentIntent.status = status;

              if (status == "succeeded") {
                localStorage.removeItem(this.cartName);
                clearInterval(this.interval);
                this.interval = null;
                clearInterval(this.intervalCountdown);
                this.intervalCountdown = null;
                localStorage.removeItem(localStorageName("DINING_OPTION"));
                // Clear promo code after successful payment
                localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
                if (this.isLogined || this.staffLogined) {
                  localStorage.removeItem(localStorageName("STAFF_INFO"));
                  localStorage.removeItem(
                    localStorageName("PASSCODE_CONFIRMED")
                  );
                  localStorage.removeItem(localStorageName("SELECTED_REWARD"));
                  memberService.logout(this.merchantCode);
                }

                OrderService.checkCheckoutSessionStripe({ stripe_id: this.paymentIntent.id }).then(
                  (response) => {
                    if (response.data.result.isSuccess) {
                      console.log('Checkout session stripe success');
                    }
                  }
                );

                this.$router.push({
                  name: "PaymentSuccessPageKiosk",
                  params: {
                    merchant_code: this.merchantCode,
                    branch_code: this.branchCode,
                    transaction_id: this.transaction.transaction_id,
                  },
                });
              }
            }
          } else {
            this.handleCheckStatusFailure(response.data.result.message);
          }
        },
        async (error) => {
          if (error.response?.data?.result?.statusCode == 400) {
            this.isRetry = true;
            this.messageNotification = error.response.data.result.message;
            $(".btn-notification-modal").click();
            clearInterval(this.interval);
            this.interval = null;
            clearInterval(this.intervalCountdown);
            this.intervalCountdown = null;
            this.paymentStatus.status = false;
            this.paymentStatus.code = error.response.data.result.statusCode;

            if (this.paymentIntent.id) {
              this.cancelPaymentIntent();
              this.cancelReaderAction();
            }
          } else {
            this.handleCheckStatusFailure(error.response?.data?.result?.message);
          }
        }
      );
    },

    // Re-validates the cart against the backend (stock/price/voucher) right
    // before the transaction is actually created — the shopper may have sat
    // on this screen for a while after leaving Checkout. Mirrors Checkout's
    // reloadOrder(); on a business-rule failure (item no longer available,
    // voucher no longer valid) there's nothing to fix here, so the shopper
    // is shown why (same as MyCart's out-of-stock / voucher messaging) and
    // sent back to Checkout to adjust the cart once they acknowledge it. A
    // transport-level failure (network/server error) just shows a message
    // and lets them retry the same button, same as before this check existed.
    async validateCartBeforePayment() {
      this.showLoader();
      let dataForm = {
        items: this.itemSelect,
        branch_code: this.branchCode,
        member_id: this.memberInfo?.id || 0,
        promocode: this.rewardSelect,
        total_item: this.calculateTotal(),
      };

      return OrderService.reload(dataForm).then(
        (response) => {
          this.hideLoader();

          if (response.data.result.isSuccess) {
            const dataResponse = response.data.data;
            this.itemSelect = dataResponse.items;

            if (dataResponse.isCanPay == 0) {
              this.redirectToCheckoutAfterNotification = true;
              this.messageNotification =
                this.$t("kiosk.payment.cartChanged");
              $(".btn-notification-modal").click();
              return false;
            }

            // Price Schedules: a price changed while the guest sat here. The order is not
            // created yet, so go back to Checkout - its own reload reprices the cart and
            // lists the changed lines. Checked on a copy: the stored cart stays unpriced
            // for that reload. (Once the order exists, isRetry skips this check on purpose.)
            if (Array.isArray(dataResponse.prices)) {
              const items = JSON.parse(JSON.stringify(dataResponse.items));
              if (applyCartPrices(items, dataResponse.prices, this.isLogined || this.staffLogined).length) {
                this.gotoPath("CheckoutPageKiosk");
                return false;
              }
            }

            return true;
          } else {
            let warningMessage = response.data.result.message;
            const prefix = "prefixVoucherError-";

            this.redirectToCheckoutAfterNotification = true;
            this.messageNotification = warningMessage.startsWith(prefix)
              ? warningMessage.substring(prefix.length)
              : warningMessage;
            $(".btn-notification-modal").click();
            return false;
          }
        },
        (error) => {
          this.hideLoader();
          this.resetPaymentUiState(error.response?.data?.result?.message);
          return false;
        }
      );
    },

    async saveOrder() {
      const canProceed = await this.validateCartBeforePayment();
      if (!canProceed) {
        return;
      }

      this.showLoader();
      let dataForm = {
        branch_code: this.branchCode,
        items: this.itemSelect,
        take_away_fee: this.totalDineInFee().toFixed(2),
        total_item: this.calculateTotal(),
        total_payment_amount: this.total - this.totalBcrs,
        gst_amount: this.convertedValueGstRate().toFixed(2),
        gst_rate: this.branch.gst_rate,
        buzzer_number: this.buzzerNumber,
        take_away_or_dine_in: this.takeAwayOrDineIn,
        member_id: this.memberInfo?.id || 0,
        is_staff: this.staffLogined ? 1 : 0,
        code: this.staffInfo?.code || "",
        total_voucher_discount: this.totalVoucherDiscount,
        voucher_code: this.rewardSelect,
        voucher_name:
          this.rewardSelect && this.rewardSelect.length > 0
            ? this.rewardSelect[0].reward_title
            : "",
        promo_code: this.promoCodeApplied?.promo_code || "",
        promo_name: this.promoCodeApplied?.promo_name || "",
        total_promo_discount: this.totalPromoDiscount,
        payment_mode: this.paymentMode,
        kiosk_payment_method: this.paymentMethod,
        bcrs_deposit: this.totalBcrs,
        bcrs_deposit_qty: this.branch?.each_bcrs_amount ? parseInt(this.totalBcrs / this.branch.each_bcrs_amount) : 0
      };

      const numericValue = parseFloat(this.branch.gst_rate);
      if (numericValue < 0) {
        dataForm.gst_amount = this.totalGSTInclusive().toFixed(2);
      }

      if (this.eWalletProcessing) {
        dataForm.select_payment_method = 1;
      }

      KioskService.saveOrder(dataForm).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            this.transaction = response.data.data;

            if (this.total == 0) {
              localStorage.removeItem(this.cartName);
              localStorage.removeItem(localStorageName("DINING_OPTION"));
              // Clear promo code after successful order
              localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
              if (this.isLogined || this.staffLogined) {
                localStorage.removeItem(localStorageName("STAFF_INFO"));
                localStorage.removeItem(localStorageName("PASSCODE_CONFIRMED"));
                localStorage.removeItem(localStorageName("SELECTED_REWARD"));
                memberService.logout(this.merchantCode);
              }

              this.$router.push({
                name: "PaymentSuccessPageKiosk",
                params: {
                  merchant_code: this.merchantCode,
                  branch_code: this.branchCode,
                  transaction_id: this.transaction.transaction_id,
                },
              });
            }

            // eWallet first: the order is already paid, nothing else may start.
            if (this.eWalletProcessing) {
              this.eWalletPaymentSuccess();
            } else if (this.isPaynowQR) {
              if (this.paymentMode == ALLINPAY) {
                this.getQRCodeAllinpay();
              } else if (this.paymentMode == AIRWALLEX) {
                this.createAirwallexPaymentIntentPaynow();
              } else {
                this.createPaymentIntentPaynow();
              }
            } else if (this.isShopBackPay) {
              this.$nextTick(() => this.startShopBackPaymentComponent());
            } else {
              if (this.paymentMode == ALLINPAY) {
                this.cardSale();
              } else if (this.paymentMode == AIRWALLEX) {
                this.createAirwallexPaymentIntent();
              } else {
                this.createPaymentIntent();
              }
            }
          } else if (isPromoCodeError(response.data.data)) {
            // The promo code no longer works (expired, out of its schedule ...) and the order
            // was not created. Like a price change (validateCartBeforePayment), go straight back
            // to Checkout instead of leaving the guest on this screen with the old amount:
            // Checkout drops the code, shows the new total and says why (KIOSK_PROMO_CODE_REMOVED).
            this.hideLoader();
            this.clearAllInterval();
            localStorage.removeItem(localStorageName("KIOSK_PROMO_CODE_APPLIED"));
            localStorage.setItem(localStorageName("KIOSK_PROMO_CODE_REMOVED"), JSON.stringify({
              code: this.promoCodeApplied?.promo_code || "",
              reason: promoReasonText(this.$t.bind(this), response.data.data, response.data.result.message),
            }));
            this.gotoPath("CheckoutPageKiosk");
          } else {
            this.hideLoader();
            this.resetPaymentUiState(response.data.result.message);
          }
        },
        (error) => {
          this.hideLoader();
          this.resetPaymentUiState(error.response?.data?.result?.message);
        }
      );
    },

    async retry() {
      this.isRetry = true;
      this.isPaymentProcessing = false;
      this.isPaymentCountdown = false;
      this.clearAllInterval();
      this.paymentStatus = {
        status: true,
        code: null,
      };

      if (this.paymentMode == ALLINPAY) {
        if (this.reqId) {
          await this.cancelCardSale();
          this.reqId = null;
        }

        if (this.isPaynowQR) {
          this.qrImage = null;
        }
      } else if (this.paymentMode == AIRWALLEX) {
        if (this.paymentIntent.id) {
          const res = await this.cancelAirwallexPaymentIntent();
          // Preserve grace-period marker if cancel was blocked
          if (!res || !res.blocked) {
            this.airwallexProcessedAt = null;
          }
        } else {
          this.airwallexProcessedAt = null;
        }
        this.airwallexPayNowQrPayload = null;
      } else {
        if (this.paymentIntent.id) {
          await this.cancelReaderAction();
          await this.cancelPaymentIntent();
        }
      }
    },

    convertedValueGstRate() {
      const numericValue = parseFloat(this.branch.gst_rate);

      if (numericValue > 0) {
        // exclusive
        // Calculate base amount for GST
        let baseAmount = this.calculateTotal();

        // If promo code applied, calculate promo discount
        let promoDiscount = 0;
        if (this.isPromoCodeApplied && this.promoCodeApplied) {
          promoDiscount = this.promoDiscountFor(baseAmount);
        }

        // Apply both voucher and promo discounts
        baseAmount = baseAmount - this.totalVoucherDiscount - promoDiscount;

        // Add service charge to base amount for GST calculation
        baseAmount += this.totalDineInFee();

        // Round to 2 decimals so display and Bill Total stay consistent
        const rawGst = baseAmount * (numericValue / 100);
        return Math.round((rawGst + Number.EPSILON) * 100) / 100;
      } else {
        // inclusive
        return 0;
      }
    },

    totalGSTInclusive() {
      var gstConverted = Math.abs(this.branch.gst_rate) / 100;
      const rawGst =
        (this.calculateTotalAfterGST() / (1 + gstConverted)) * gstConverted;
      // Round half-up to 2 decimals (avoid `.toFixed(2)` rounding 0.495 down to 0.49).
      return Math.round((rawGst + Number.EPSILON) * 100) / 100;
    },

    calculateTotalAfterGST() {
      if (this.itemSelect && this.itemSelect.length > 0) {
        if (parseFloat(this.branch.gst_rate) < 0) {
          // INCLUSIVE
          let total = this.itemSelect.reduce((total, product) => {
            if (this.isLogined || this.staffLogined) {
              total += product.member_price
                ? product.member_price * product.quantity
                : 0;
            } else {
              total += product.price_default
                ? product.price_default * product.quantity
                : 0;
            }

            return total;
          }, 0);

          total += this.totalDineInFee() - this.totalVoucherDiscount;
          return total > 0 ? total : 0;
        } else {
          // EXCLUSIVE
          let total = this.itemSelect.reduce((total, product) => {
            if (this.isLogined || this.staffLogined) {
              total += product.member_price
                ? product.member_price * product.quantity
                : 0;
            } else {
              total += product.price_default
                ? product.price_default * product.quantity
                : 0;
            }

            return total;
          }, 0);

          total +=
            this.totalDineInFee() +
            this.convertedValueGstRate() -
            this.totalVoucherDiscount;
          return total > 0 ? total : 0;
        }
      } else {
        return 0;
      }
    },

    totalDineInFee() {
      if (this.takeAwayOrDineIn == 2 && this.branch.service_charge_rate > 0) {
        // Calculate base amount for service charge
        let baseAmount = this.calculateTotal();

        // If promo code applied, calculate promo discount
        let promoDiscount = 0;
        if (this.isPromoCodeApplied && this.promoCodeApplied) {
          promoDiscount = this.promoDiscountFor(baseAmount);
        }

        // Apply both voucher and promo discounts
        baseAmount = baseAmount - this.totalVoucherDiscount - promoDiscount;

        return baseAmount * (this.branch.service_charge_rate / 100);
      } else {
        return 0;
      }
    },

    // M977: whole bill, or only the items of the promo's report groups.
    promoDiscountFor(billSubtotal) {
      return promoDiscountAmount(
        this.promoCodeApplied,
        billSubtotal,
        this.itemSelect,
        this.isLogined || this.staffLogined
      );
    },
    calculateTotal() {
      if (this.itemSelect && this.itemSelect.length > 0) {
        return this.itemSelect.reduce((total, product) => {
          if (this.isLogined || this.staffLogined) {
            total += product.member_price
              ? product.member_price * product.quantity
              : 0;
          } else {
            total += product.price_default
              ? product.price_default * product.quantity
              : 0;
          }

          return total;
        }, 0);
      } else {
        return 0;
      }
    },

    // calculateTotalBeforeDiscount() {
    //   if (this.itemSelect && this.itemSelect.length > 0) {
    //     if (parseFloat(this.branch.gst_rate) < 0) {
    //       // INCLUSIVE
    //       let total = this.itemSelect.reduce((total, product) => {
    //         if (this.isLogined || this.staffLogined) {
    //           total += product.member_price
    //             ? product.member_price * product.quantity
    //             : 0;
    //         } else {
    //           total += product.price_default
    //             ? product.price_default * product.quantity
    //             : 0;
    //         }

    //         return total;
    //       }, 0);

    //       total += this.totalDineInFee();
    //       return total > 0 ? total : 0;
    //     } else {
    //       // EXCLUSIVE
    //       let total = this.itemSelect.reduce((total, product) => {
    //         if (this.isLogined || this.staffLogined) {
    //           total += product.member_price
    //             ? product.member_price * product.quantity
    //             : 0;
    //         } else {
    //           total += product.price_default
    //             ? product.price_default * product.quantity
    //             : 0;
    //         }

    //         return total;
    //       }, 0);

    //       total += this.totalDineInFee() + this.convertedValueGstRate();
    //       return total > 0 ? total : 0;
    //     }
    //   } else {
    //     return 0;
    //   }
    // },

    finalTotal() {
      // if promo code applied, calculate with discount
      if (this.isPromoCodeApplied && this.promoCodeApplied) {
        return this.calculateFinalTotalWithPromoCode().toFixed(2);
      }
      // if no promo code, calculate normally
      return this.calculateTotalAfterGST().toFixed(2);
    },
    totalPayable() {
      const finalTotalNum = parseFloat(this.finalTotal()) || 0;
      const bcrsNum = parseFloat(this.totalBcrs) || 0;
      return (finalTotalNum + bcrsNum).toFixed(2);
    },
    calculateFinalTotalWithPromoCode() {
      if (!this.promoCodeApplied) {
        return this.calculateTotalAfterGST();
      }

      // Calculate base total (subtotal)
      let baseTotal = this.calculateTotal();
      
      // Calculate promo discount based on type
      let promoDiscount = 0;
      promoDiscount = this.promoDiscountFor(baseTotal);
      
      // Apply both voucher and promo discounts
      let finalAmount = baseTotal - this.totalVoucherDiscount - promoDiscount;

      // add service charge if there is
      if (this.takeAwayOrDineIn == 2 && this.branch.service_charge_rate > 0) {
        const serviceCharge = finalAmount * (this.branch.service_charge_rate / 100);
        finalAmount += serviceCharge;
      }

      // add tax if it is EXCLUSIVE (gst_rate > 0)
      const numericValue = parseFloat(this.branch.gst_rate);
      if (numericValue > 0) {
        const gstAmount = finalAmount * (numericValue / 100);
        finalAmount += gstAmount;
      }

      return finalAmount > 0 ? finalAmount : 0;
    },

    async calculateVoucherPrice() {
      if (
        this.memberInfo &&
        this.rewardSelect &&
        this.rewardSelect.length > 0
      ) {
        this.showLoader();
        let dataForm = {
          branch_code: this.branch.branch_code,
          memberId: this.memberInfo?.id || 0,
          voucherCode: this.rewardSelect[0].code,
          totalOrder: this.calculateTotal(),
          items: this.itemSelect,
          from_kiosk: 1,
        };

        await PromocodeService.calculateVoucherPrice(dataForm).then(
          (response) => {
            if (response.data.result.isSuccess) {
              if (this.rewardSelect[0].reward_type == FREE_ITEM) {
                if (this.rewardSelect[0].item_selected) {
                  this.itemSelect.push(this.rewardSelect[0].item_selected);
                }
              } else {
                this.totalVoucherDiscount = response.data.data.voucher_deducted;
                const errMessage = response.data.data.error_message;

                if (errMessage) {
                  this.voucherError(errMessage);
                }
              }
            } else {
              this.voucherError(response.data.result.message);
            }
            this.hideLoader();
          },
          (error) => {
            this.voucherError(error.response.data.result.message);
            this.hideLoader();
          }
        );
      }
    },

    voucherError(errMessage) {
      $(".btn-hide-modal-input-buzzer-number").click();
      this.messageNotification = errMessage + "<br>" + this.$t("kiosk.payment.voucherRemoved");
      $(".btn-notification-modal").click();

      this.rewardSelect = null;
      this.totalVoucherDiscount = 0;
      localStorage.removeItem(localStorageName("SELECTED_REWARD"));
    },

    async closeModalNotificationHandler() {
      if (this.errorOnlyNotification) {
        this.errorOnlyNotification = false;
        return;
      }

      if (this.redirectToCheckoutAfterNotification) {
        this.redirectToCheckoutAfterNotification = false;
        this.gotoPath("CheckoutPageKiosk");
        return;
      }

      if (this.showBuzzerNumber && this.buzzerNumber == "") {
        $(".btn-show-modal-input-buzzer-number").click();
      }

      this.isPaymentProcessing = false;
      this.isPaymentCountdown = false;

      if (this.paymentMode == ALLINPAY && this.messageNotification == 'Timeout Error') {
        if (this.paymentMethod == 'Credit Card') {
          await this.cardQuery();

          this.paymentStatus = {
            status: true,
            code: null,
          };
        } else if (this.paymentMethod == 'Paynow') {
          this.qrCodeQuery();
        }
      }

      // Airwallex: final status check after timeout to handle
      // case where payment succeeded right before timeout fired
      if (this.paymentMode == AIRWALLEX) {
        if (this.messageNotification == 'Timeout Error' && this.paymentIntent.id) {
          await this.getAirwallexPaymentIntentStatus();
        }

        this.paymentStatus = {
          status: true,
          code: null,
        };
      }

      this.reqId = null;
    },

    async getStaffRemainingSpending() {
      var dataForm = {
        staff_id: this.staffInfo?.id || 0,
      };

      await KioskService.getStaffRemainingSpending(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.staffInfo.remaining_spending =
              response.data.data.staff_remaining_spending;

            if (
              parseFloat(this.staffInfo.remaining_spending) <
              parseFloat(this.total)
            ) {
              this.$router.go(-1);
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error.response.data.result.message;
          this.setTimeOutNotification();
        }
      );
    },

    clearAllInterval() {
      clearInterval(this.interval);
      this.interval = null;
      clearInterval(this.intervalCountdown);
      this.intervalCountdown = null;
      clearInterval(this.timeoutRetryInterval);
      this.timeoutRetryInterval = null;
      // Zero it as well, so a stray clearAllInterval() while the timeout screen
      // is up leaves Retry enabled rather than frozen at "Retry (7)" forever —
      // a dead end on a screen that has no other button.
      this.timeoutRetryCountdown = 0;
      // clearInterval(this.intervalConnectReader);
      // this.intervalConnectReader = null;
      // clearTimeout(this.timeoutId);
      // this.timeoutId = null
    },

    /**
     * Arm the timer that hides the Cancel button after `ms`. Clears any
     * previously armed timer first — otherwise a leftover timer from an earlier
     * payment attempt can fire partway through a retry and hide the button
     * early (e.g. Cancel showing only ~3s on the 2nd attempt instead of 10s).
     */
    _armCancelButtonTimeout(ms = 30000) {
      clearTimeout(this.cancelOrderTimeout);
      this.cancelOrderTimeout = setTimeout(() => {
        this.allowCancelOrder = false;
      }, ms);
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    logout() {
      memberService.logout(this.merchantCode);
      this.memberInfo = null;
      this.isLogined = false;
      this.staffLogined = false;
    },

    getPromoDiscountAmount() {
      if (!this.isPromoCodeApplied || !this.promoCodeApplied) {
        return 0;
      }

      // Calculate base total (subtotal)
      let baseTotal = this.calculateTotal();

      return this.promoDiscountFor(baseTotal);
    },

    sendOtpToWhatsApp() {
      console.log("sendOtpToWhatsApp");
      $(".btn-show-modal-enter-otp").click();
    },

    openModalEnterOtp() {
      $(".btn-show-modal-enter-otp").click();
    },
  },
};
</script>

<style scoped>
/* KSK013 — S4 Payment Selection (theme 2): method list + bottom bar. */
.ksk-ps__scroll {
  position: fixed;
  top: var(--ksk-header-height);
  left: 0;
  right: 0;
  bottom: calc(112px + var(--ksk-powered-height));
  overflow-y: auto;
  padding: var(--ksk-space-3);
  background: var(--ksk-background);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}
.ksk-ps__scroll::-webkit-scrollbar {
  display: none;
}

/* Summary row: label + item count left, amount right; same centred column
   as the methods below. */
.ksk-ps__amount {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ksk-space-3);
  padding: 20px var(--ksk-space-3);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-card);
}
.ksk-ps__amount-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ksk-ps__amount-label {
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  font-weight: 600;
  color: var(--ksk-text);
}
.ksk-ps__amount-meta {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-ps__amount-value {
  font-size: 56px;
  line-height: 64px;
  font-weight: 800;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--ksk-primary);
}

.ksk-ps__title {
  margin: var(--ksk-space-6) 0 var(--ksk-space-3);
  text-align: center;
  font-family: var(--ksk-font);
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ksk-text);
}

/* Portrait screen: methods stacked in one centred column (not full width),
   each a row with a big logo on the left. */
.ksk-ps__methods {
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-2);
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}

.ksk-ps-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--ksk-space-3);
  min-height: 152px;
  padding: var(--ksk-space-3);
  /* KSK063: payment_method colour slot (outline = card ring, font = title,
     accent = balance, chevron, pressed ring); theme values otherwise. */
  border: 2px solid var(--ksk-slot-outline, transparent);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-card);
  cursor: pointer;
  transition: transform 80ms ease-out, border-color 80ms ease-out;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.ksk-ps-card:active {
  transform: scale(0.98);
  border-color: var(--ksk-slot-accent, var(--ksk-primary));
}
/* Not tappable: the eWallet card keeps its old pointer-events: none.
   Listed after the methods that can be used (visual order only). */
.ksk-ps-card--disabled {
  order: 1;
  pointer-events: none;
  cursor: not-allowed;
  background: var(--ksk-surface-muted);
  box-shadow: none;
}
.ksk-ps-card--disabled .ksk-ps-card__icon,
.ksk-ps-card--disabled .ksk-ps-card__title,
.ksk-ps-card--disabled .ksk-ps-card__desc,
.ksk-ps-card--disabled .ksk-ps-card__caption,
.ksk-ps-card--disabled .ksk-ps-card__logos {
  opacity: 0.55;
}

.ksk-ps-card__icon {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 156px;
  height: 156px;
  border-radius: 32px;
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-ps-card__icon .material-icons-round {
  font-size: 90px;
}
/* Vivid "app icon" tiles so each method is recognisable at a glance. */
.ksk-ps-card__icon--contactless {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  color: #ffffff;
  box-shadow: 0 8px 20px rgba(79, 70, 229, 0.3);
}
/* eWallet: same build as the contactless tile (vivid gradient + white disc
   with a coloured symbol), in its own teal. Fixed colours, not the merchant
   brand, so it never turns dull next to the other logos. */
.ksk-ps-card__icon--wallet {
  background: linear-gradient(135deg, #14b8a6 0%, #059669 100%);
  box-shadow: 0 8px 20px rgba(5, 150, 105, 0.3);
}
.ksk-ps-card__disc {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #ffffff;
  color: #059669;
}
.ksk-ps-card__icon .ksk-ps-card__disc .material-icons-round {
  font-size: 44px;
}
/* Brand logo on white (PayNow): framed so it reads as a tile too. */
.ksk-ps-card__icon--logo {
  border: 1px solid var(--ksk-border);
  background: #ffffff;
}
.ksk-ps-card__icon--image {
  overflow: hidden;
  background: transparent;
}
.ksk-ps-card__icon--image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ksk-ps-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ksk-ps-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.ksk-ps-card__title {
  font-size: 28px;
  line-height: 36px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ksk-slot-font, var(--ksk-text));
}
.ksk-ps-card__desc {
  font-size: 20px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
}
.ksk-ps-card__balance {
  font-weight: 700;
  color: var(--ksk-slot-accent, var(--ksk-primary));
}
.ksk-ps-card__caption {
  font-size: 16px;
  line-height: 22px;
  color: var(--ksk-text-secondary);
}
.ksk-ps-card__pill {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  color: var(--ksk-text-secondary);
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}
.ksk-ps-card__pill--error {
  background: var(--ksk-error);
  color: var(--ksk-on-status);
}

/* Accepted brands under the text. */
.ksk-ps-card__logos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: var(--ksk-space-1);
}
.ksk-ps-card__logos img {
  width: 56px;
  height: 36px;
  padding: 4px;
  object-fit: contain;
  border: 1px solid var(--ksk-border);
  border-radius: 8px;
  background: #ffffff;
}
/* Contactless card brands read a size up from the PayNow wallet row. */
.ksk-ps-card__logos--card {
  gap: 8px;
}
.ksk-ps-card__logos--card img {
  width: 72px;
  height: 46px;
}

.ksk-ps-card__chevron {
  flex: none;
  font-size: 44px;
  color: var(--ksk-slot-accent, var(--ksk-text-secondary));
}
.ksk-ps-card--disabled .ksk-ps-card__chevron {
  visibility: hidden;
}

.ksk-ps__empty {
  padding-top: var(--ksk-space-4);
}

.ksk-ps__bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: var(--ksk-powered-height);
  z-index: 3;
  display: flex;
  gap: var(--ksk-space-2);
  height: 112px;
  padding: var(--ksk-space-2) var(--ksk-space-3);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-bar);
}
.ksk-ps__bar-btn {
  flex: 1 1 0;
}
.ksk-ps__start-over {
  color: var(--ksk-error);
}

/* KSK014/KSK015 — payment in progress (theme 2). */
.ksk-pp__scroll {
  position: fixed;
  top: var(--ksk-header-height);
  left: 0;
  right: 0;
  bottom: calc(112px + var(--ksk-powered-height));
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: var(--ksk-space-3);
  background: var(--ksk-background);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}
.ksk-pp__scroll--no-bar {
  bottom: var(--ksk-powered-height);
}
.ksk-pp__scroll::-webkit-scrollbar {
  display: none;
}
/* Area in the method list's place: same 760px column, filling the height
   below the amount row; no panel of its own, content sits on the page. */
.ksk-pp__card {
  flex: 1 0 auto;
  width: 100%;
  max-width: 760px;
  margin: var(--ksk-space-3) auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Fixed top offset (not a share of the free height), so the title sits
     at the same height for every method whatever the content below. */
  padding: 64px 0 var(--ksk-space-4);
  text-align: center;
}
.ksk-pp__state {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
/* Card tap artwork (laid out by .ksk-pay__stage, kiosk-theme2.css). */
.ksk-pp__tap-art svg {
  max-width: 560px;
  display: block;
  width: 100%;
  height: auto;
}
.ksk-pp__tap-reader {
  fill: var(--ksk-surface);
  stroke: var(--ksk-border);
  stroke-width: 2;
}
.ksk-pp__tap-waves path {
  fill: none;
  stroke: var(--ksk-primary);
  stroke-width: 6;
  stroke-linecap: round;
  animation: ksk-pp-wave 1.6s ease-in-out infinite;
}
.ksk-pp__tap-waves path:nth-child(2) {
  animation-delay: 0.2s;
}
.ksk-pp__tap-waves path:nth-child(3) {
  animation-delay: 0.4s;
}
.ksk-pp__tap-card {
  animation: ksk-pp-tap 1.6s ease-in-out infinite;
}
.ksk-pp__tap-card rect {
  fill: var(--ksk-primary);
}
.ksk-pp__tap-card .ksk-pp__tap-chip {
  fill: #e3c46a;
}
@keyframes ksk-pp-tap {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(14px); }
}
@keyframes ksk-pp-wave {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}

/* Allinpay timeout copy (v-html, several lines): title-sized so it reads from
   a step back. Doubled class to outrank the theme's .ksk-pay__caption. */
.ksk-pay__caption.ksk-pp__allinpay-text {
  max-width: 860px;
  font-size: var(--ksk-title-size);
  line-height: 44px;
  font-weight: 600;
  color: var(--ksk-text);
}
.ksk-pp__allinpay-retry {
  margin-top: var(--ksk-space-3);
  font-size: var(--ksk-label-size);
  font-weight: 600;
  /* Not --ksk-primary: that is the merchant's brand colour, often too light
     to read as text on white. */
  color: var(--ksk-text);
}
.ksk-pp__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-2);
  min-height: 56px;
  padding: 0 var(--ksk-space-3);
  border: 1px solid var(--ksk-border);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  font-size: var(--ksk-body-size);
  font-weight: 600;
  color: var(--ksk-text);
}
/* On the in-progress screens the page loader shows nothing (they have their
   own inline status): no dots, no KSK037 scrim + "Please wait…" card. The
   transparent overlay still blocks taps while a request runs. */
.ksk-pp__loader--inline :deep(.loader),
.ksk-pp__loader--inline :deep(.loader-ksk-card) {
  display: none !important;
}
.app-kiosk-mode .ksk-pp__loader--inline {
  background: transparent !important;
  animation: none !important;
}
/* Small inline spinner (Connecting / ShopBack waiting). */
.ksk-pp__spinner {
  border: 4px solid var(--ksk-border);
  border-top-color: var(--ksk-primary);
  border-radius: 50%;
  animation: ksk-pp-spin 0.9s linear infinite;
}
.ksk-pp__spinner--sm {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-width: 4px;
}
@keyframes ksk-pp-spin {
  to { transform: rotate(360deg); }
}

/* Bottom bar */
.ksk-pp__bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: var(--ksk-powered-height);
  z-index: 3;
  display: flex;
  align-items: center;
  height: 112px;
  padding: var(--ksk-space-2) var(--ksk-space-3);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-bar);
  font-family: var(--ksk-font);
}
.ksk-pp__bar-row {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  width: 100%;
}
.ksk-pp__bar-row--center {
  justify-content: center;
}
.ksk-pp__btn {
  flex: 1 1 0;
}
.ksk-pp__btn--danger {
  color: var(--ksk-error);
}
.ksk-pp__countdown {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  color: var(--ksk-text-secondary);
  text-align: left;
}
.ksk-pp__waiting {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-2);
  height: 80px;
  padding: 0 var(--ksk-space-3);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface-muted);
  font-size: var(--ksk-label-size);
  font-weight: 600;
  color: var(--ksk-text);
}

@media (prefers-reduced-motion: reduce) {
  .ksk-pp__tap-card,
  .ksk-pp__tap-waves path {
    animation: none;
  }
}

.box-otp {
  width: 420px;
  border: 1px solid #d3d3d3;
  border-radius: 15px;
  padding: 15px;
}

.box-otp div {
  font-weight: bold;
}

.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.btn-enter-otp {
  background-color: var(--color);
  color: white;
}

.dots-animation::after {
  content: '...';
  animation: dots 1.5s steps(4, end) infinite;
  display: inline-block;
  width: 20px;
  text-align: left;
}

@keyframes dots {
  0%, 20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%, 100% {
    content: '...';
  }
}
</style>
